import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit'
import { createApi } from '@reduxjs/toolkit/query/react'
import { undoable } from './undoable';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import React from 'react';
import { isBuiltInTriggerApp, normalizeBuiltInAppUrlsInElements, resolveAgentTriggerPerformList } from '../../constants/triggerApps';
import { repairFrontendWorkflow } from '../../workflow/repairFrontendWorkflow';

const defaultState = {
    id: "0",
    name: "Untitled Workflow",
    loading: 'unloaded',
    active: false,
    currentElementID: null,
    elements: [],
    error: null,
    errorDetails: null,
    tenantSelection: null
}

const filterFields = [
    {
        "name": "Filter Name",
        "schema": {
            "type": "string"
        }
    },
    {
        "name": "Only continue if...",
        "schema": {
            "title": "Filter Setup",
            "type": "array",
            "items": {
                "title": "Conditions",
                "type": "array",
                "items": {
                    "title": "Only continue if...",
                    "type": "object",
                    "properties": {
                        "lhs": {
                            "title": "Choose Field...",
                            "type": "string"
                        },
                        "conditionType": {
                            "title": "Choose condition...",
                            "enum": [
                                "stringEqual",
                                "stringUnequal",
                                "listContains",
                                "listNotContain",
                                "greaterThan",
                                "lessThan",
                                "isTrue",
                                "isFalse",
                                "valueExists",
                                "valueNotExists"
                            ],
                            "enumNames":[
                                "(Text/Number) Equals",
                                "(Text/Number) Does not equal",
                                "(Text/Array) Contains",
                                "(Text/Array) Does not contain",
                                "(Number) Greater than",
                                "(Number) Less Than",
                                "(Boolean) Is true",
                                "(Boolean) Is false",
                                "Exists",
                                "Does not exist"
                            ],
                            "type": "string"
                        },
                        "rhs": {
                            "title": "Enter or select value...",
                            "type": "string"
                        }
                    }
                }
            }
        }
    },
    {
        "name": "Delay",
        "schema": {
            "title": "Delay Setup (seconds)",
            "type": "string"
        }
    }
]

const defaultWorkflow = (name) => {
    return {
        name: name ?? 'Untitled Workflow',
        description: '',
        active: false,
        nodesRun: 0,
        flowControl: [],
        nodes: {}
    }
}

const axiosBaseQuery = ({ baseUrl } = { baseUrl: '' }) =>
    async ({ endpoint, method, headers, params, data }) => {
        try {
            const result = await axios({ url: baseUrl + endpoint, method, headers, params, data })
            return { data: result.data }
        } catch (axiosError) {
            let err = axiosError
            return {
                error: { status: err.response?.status, data: err.response?.data }
            }
        }
    }

export const workflowApi = createApi({
    reducerPath: 'workflowApi',
    baseQuery: axiosBaseQuery({
        baseUrl: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production'
    }),
    endpoints: build => ({
        getWorkflow: build.query({
            query: (id) => ({
                endpoint: '/djangocaller',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                params: {},
                data: {
                    operation: 'getWorkflow',
                    id: id
                }
            }),
            transformResponse: response => response.data
        }),
        createWorkflow: build.query({
            query: (name) => ({
                endpoint: '/djangocaller',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                params: {},
                data: {
                    operation: 'createWorkflow',
                    data: {
                        name: name,
                        active: false,
                        current_state: {
                            frontendWorkflow: [],
                            workflowTestbed: defaultWorkflow(name),
                            workflowTemplate: defaultWorkflow(name)
                        }
                    }
                }
            }),
            transformResponse: response => response.data
        }),
        saveWorkflow: build.query({
            query: (id, wf) => ({
                endpoint: '/djangocaller',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                params: {},
                data: {
                    operation: 'saveWorkflow',
                    id: id,
                    data: wf
                }
            }),
            transformResponse: response => response.data
        }),
    })
})

export const fetchWorkflow = createAsyncThunk('workflow/fetchWorkflow', async (djangoId, { getState, rejectWithValue }) => {
    const loading = getState().workflow.present.loading
    if (loading == 'loading') {
        try {
            var djangoData = {
                operation: 'getWorkflow',
                id: djangoId
            }

            var config = {
                method: 'post',
                url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: djangoData
            }
            const resp = await axios(config)
            const fw = resp.data.current_state.frontendWorkflow || []
            const repaired = repairFrontendWorkflow(Array.isArray(fw) ? fw : [])
            const elements = normalizeBuiltInAppUrlsInElements(repaired)
            return {
                id: djangoId,
                name: resp.data.name,
                elements,
                loading: 'loaded',
                active: resp.data.current_state.workflowTemplate.active
            }
        } catch (error) {
            return rejectWithValue({
                error: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
        }
    } else {
        return rejectWithValue({
            error: 'Workflow already loading',
            status: 400
        });
    }
})

export const saveWorkflow = createAsyncThunk('workflow/saveWorkflow', async (a, { getState }) => {
    const state = getState().workflow.present

    // CRITICAL: Merge with workflow from server before saving, so we never overwrite the trigger's
    // testData/triggerChoices/credential/etc. (same as testNode).
    let elementsToSave = state.elements
    try {
        const getResp = await axios({
            method: 'post',
            url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
            headers: { 'Content-Type': 'application/json' },
            data: { operation: 'getWorkflow', id: state.id }
        })
        const serverWf = getResp.data?.current_state?.frontendWorkflow
        if (Array.isArray(serverWf) && serverWf.length > 0) {
            const isNode = (el) => el && el.id && !el.source && !el.target
            elementsToSave = state.elements.map((el) => {
                if (!isNode(el)) return el
                const serverEl = serverWf.find((s) => s.id === el.id && isNode(s))
                if (!serverEl) return el
                const serverData = serverEl.data || serverEl
                const localData = el.data || el
                const hasServerTriggerData = serverData.testData != null || (Array.isArray(serverData.triggerChoices) && serverData.triggerChoices.length > 0)
                const localHasPayload = localData.testData && typeof localData.testData === 'object' && Object.keys(localData.testData).length > 0
                if (hasServerTriggerData && (el.type === 'trigger' || el.type === 'polling')) {
                    return {
                        ...el,
                        data: {
                            ...localData,
                            // Prefer local testData when it has actual payload (e.g. webhook just received); server may have testData: {}
                            testData: localHasPayload ? localData.testData : (serverData.testData ?? localData.testData),
                            triggerChoices: serverData.triggerChoices ?? localData.triggerChoices,
                            triggerchoice: localHasPayload ? (localData.triggerchoice ?? serverData.triggerchoice) : (serverData.triggerchoice ?? localData.triggerchoice),
                            triggerChoiceTime: localHasPayload ? (localData.triggerChoiceTime ?? serverData.triggerChoiceTime) : (serverData.triggerChoiceTime ?? localData.triggerChoiceTime),
                            credential: serverData.credential ?? localData.credential,
                            authname: serverData.authname ?? localData.authname,
                            // Prefer local fields so override/config edits in the UI are persisted to the backend
                            fields: localData.fields ?? serverData.fields
                        }
                    }
                }
                return el
            })
        }
    } catch (e) {
        // If fetch fails, fall back to state.elements
    }

    // Scrub empty keys from body fields so removed fields (e.g. start_date: "") are never persisted or sent to test
    elementsToSave = scrubBodyValuesInElements(elementsToSave);
    // Ensure app and triggerKey so agent check_workflow_status sees configured nodes
    elementsToSave = ensureNodeKeysForAgent(elementsToSave);
    // Normalize webhook (and other built-in) trigger keys so saved workflow has correct triggerKey/triggerResource/data.type
    elementsToSave = normalizeBuiltInAppUrlsInElements(elementsToSave);
    // Built-in trigger apps: clear performList so backend does not call getDD (500). Webhook must keep triggerResource='createWebhook' and triggerKey='webhookReceived'.
    elementsToSave = elementsToSave.map((el) => {
        if (el.type !== 'trigger' || !el.data) return el;
        if (isBuiltInTriggerApp(el.data.app)) {
            const app = el.data.app;
            const data = { ...el.data, performList: '' };
            if (app !== 'webhook') {
                data.triggerResource = '';
            }
            return { ...el, data };
        }
        return el;
    });

    var djangoData = {
        operation: 'updateWorkflow',
        id: state.id,
        data: {
            name: state.name,
            current_state: {
                frontendWorkflow: elementsToSave,
                workflowTestbed: { nodesRun: 0, flowControl: [], nodes: convertToBackendNodes(elementsToSave), globals: {} },
                workflowTemplate: { nodesRun: 0, active: state.active, flowControl: [], nodes: convertToBackendNodes(elementsToSave), globals: {} }
            }
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }
    const resp = await axios(config)
    const fw = resp.data.current_state.frontendWorkflow || []
    return {
        id: state.id,
        name: resp.data.name,
        elements: repairFrontendWorkflow(Array.isArray(fw) ? fw : [])
    }
    
})

export const testNode = createAsyncThunk('workflow/testNode', async (nodeID, { getState }) => {
   
    const state = getState().workflow.present

   // CRITICAL: Merge with workflow from server before saving, so we never overwrite the trigger's
   // testData/triggerChoices/credential/etc. when testing the action step (Redux can have a stripped trigger).
   let elementsToSave = state.elements
   try {
       const getResp = await axios({
           method: 'post',
           url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
           headers: { 'Content-Type': 'application/json' },
           data: { operation: 'getWorkflow', id: state.id }
       })
       const serverWf = getResp.data?.current_state?.frontendWorkflow
       if (Array.isArray(serverWf) && serverWf.length > 0) {
           const isNode = (el) => el && el.id && !el.source && !el.target
           // Use server's frontendWorkflow as base; replace only the node we're testing with state.elements version (latest fields).
           const localNode = state.elements.find((e) => e.id === nodeID && !e.source && !e.target)
           const serverNode = serverWf.find((e) => e.id === nodeID && isNode(e))
           elementsToSave = serverWf.map((el) => {
               if (el.id !== nodeID || !isNode(el)) return el
               // Prefer server node whenever it has body mapped — so we never overwrite good DB state with stale Redux (e.g. when agent sent fieldUpdated + runTestStep and Redux hadn't applied the update yet). Same outcome as clicking "Test action" in the panel.
               if (serverNode && nodeHasBodyMapped(serverNode)) return serverNode
               if (localNode) return localNode
               return el
           })
           // When adding a step via agent, the new node may not be in Django yet; include local node and its edges so testNode runs against the full workflow.
           if (localNode && !serverNode) {
               const newEdges = state.elements.filter((e) => e.source && e.target && (e.source === nodeID || e.target === nodeID))
               elementsToSave = [...elementsToSave, localNode, ...newEdges]
           }
       }
   } catch (e) {
       // If fetch fails, fall back to state.elements
   }

   // Scrub empty keys from body fields so removed fields (e.g. start_date: "") are never persisted or sent to test
   elementsToSave = scrubBodyValuesInElements(elementsToSave);
   // Ensure app and triggerKey so agent check_workflow_status sees configured nodes
   elementsToSave = ensureNodeKeysForAgent(elementsToSave);
   // Built-in trigger apps must not send performList/triggerResource or backend will call getDD and return 500
   elementsToSave = elementsToSave.map((el) => {
       if (el.type !== 'trigger' || !el.data) return el;
       if (isBuiltInTriggerApp(el.data.app)) {
           return { ...el, data: { ...el.data, performList: '', triggerResource: '' } };
       }
       return el;
   });

   // Debug: log the node we're about to test. First is frontend shape (what we save in frontendWorkflow); second is backend shape (what we save in workflowTestbed.nodes — this is what Django uses to run the test).
   const nodeBeforeTest = elementsToSave.find((e) => e.id === nodeID && !e.source && !e.target);
   if (nodeBeforeTest) {
       const bodyField = nodeBeforeTest.data?.fields?.find((f) => f && f.name === 'body');
       const pathFields = (nodeBeforeTest.data?.fields || []).filter((f) => f && f.in === 'path').map((f) => ({ name: f.name, value: f.value }));
       console.log('[testNode] Node before test (frontend shape — frontendWorkflow):', {
           nodeID,
           app: nodeBeforeTest.data?.app,
           name: nodeBeforeTest.data?.name,
           bodyField: bodyField ? { name: bodyField.name, valueKeys: bodyField.value && typeof bodyField.value === 'object' ? Object.keys(bodyField.value) : [], value: bodyField.value } : null,
           pathFields,
       });
       const backendNodes = convertToBackendNodes(elementsToSave);
       const backendNode = backendNodes[nodeID];
       if (backendNode) {
           const bodyInBackend = backendNode.inputFields?.fields?.find((f) => f && f.name === 'body');
           console.log('[testNode] Node before test (backend shape — workflowTestbed.nodes, what Django uses):', {
               nodeID,
               inputFieldsKeys: Object.keys(backendNode.inputFields || {}),
               bodyInInputFields: bodyInBackend ? { valueKeys: bodyInBackend.value && typeof bodyInBackend.value === 'object' ? Object.keys(bodyInBackend.value) : [], value: bodyInBackend.value } : null,
               outputFieldsKeys: Object.keys(backendNode.outputFields || {}),
           });
       }
   }

   var djangoData = {
       operation: 'updateWorkflow',
       id: state.id,
       data: {
           name: state.name,
           current_state: {
               frontendWorkflow: elementsToSave,
               workflowTestbed: { nodesRun: 0, flowControl: [], nodes: convertToBackendNodes(elementsToSave), globals: {} },
               workflowTemplate: { nodesRun: 0, active: state.active, flowControl: [], nodes: convertToBackendNodes(elementsToSave), globals: {} }
           }
       }
   }

   var config = {
       method: 'post',
       url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
       headers: {
           'Content-Type': 'application/json'
       },
       data: djangoData
   }
    await axios(config)

    // Brief delay so the backend sees the workflow we just saved before it loads it for testNode (avoids race when agent triggers test right after fieldUpdated + backend save).
    await new Promise((r) => setTimeout(r, 250))

    var djangoData = {
        operation: 'testNode',
        data: {
            workflow_id: state.id,
            start_node: nodeID,
            test: true,
            start_node_data: { 'testing': true }
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }
    const resp = await axios(config)
    Object.keys(resp.data).forEach(key => {
        if (Array.isArray(resp.data[key]) === true && resp.data[key].length > 50 ) {
           
            resp.data['Message'] = "Results were truncated to show only 50 of "+resp.data[key].length
           var new_arr = resp.data[key].slice(0, 50)
             resp.data[key] = new_arr
        }
      });

  // check if resp.data is an array
  // if it is, use resp.data.slice(0, <max-size>)

    // Debug: log raw response. If keys are empty / sample is {}, the djangocaller testNode endpoint returned an empty body — fix is in the backend (Django/Lambda), not the frontend.
    const isEmptyResponse = !resp.data || (typeof resp.data === 'object' && Object.keys(resp.data).length === 0);
    if (isEmptyResponse) {
        console.warn('[testNode] Backend returned empty response ({}). The workflow was saved with the correct node; testNode(workflow_id, start_node) is returning no body. Check the djangocaller/testNode implementation (template resolution, ClickUp call, and what it returns).');
    }
    console.log('[testNode] Raw API response:', { nodeID, keys: Object.keys(resp.data || {}), sample: resp.data });

    // Backend (Django/Lambda) may return the real result in a nested key; use it so we don't store {} when the test actually succeeded
    let testData = resp.data
    if (resp.data && typeof resp.data === 'object' && (testData == null || (typeof testData === 'object' && Object.keys(testData).length === 0))) {
        const nested = resp.data.data ?? resp.data.testData ?? resp.data.result ?? resp.data.response ?? resp.data.body ?? resp.data.output
        if (nested && typeof nested === 'object' && Object.keys(nested).length > 0) {
            testData = nested
            console.log('[testNode] Using nested payload as testData:', { keys: Object.keys(testData) })
        }
    }
    // If top-level looks like task/API response (id, name, url, etc.), keep as-is
    if (resp.data && typeof resp.data === 'object' && testData === resp.data && ['id', 'name', 'url', 'list', 'status', 'creator'].some((k) => k in resp.data)) {
        testData = resp.data
    }

    return {
        nodeID: nodeID,
        testData: testData
    }
})

export const createTrigger = createAsyncThunk('workflow/createTrigger', async (djangoId, { getState }) => {
    const state = getState().workflow.present

    let currentNode = state.elements.find(item => item.type == 'trigger')
   
    // CRITICAL FIX: For polling triggers, if triggerResource is empty, use performList
    // performList IS the triggerResource for polling triggers
    let triggerResourceValue = currentNode.data.triggerResource;
    if (!triggerResourceValue || triggerResourceValue === '') {
        // If triggerResource is empty, try to get it from performList
        if (currentNode.data.performList && currentNode.data.performList !== '') {
            triggerResourceValue = currentNode.data.performList;
        }
    }

    // Helper function to extract primitive value from potentially nested {label, value} objects
    const extractValue = (fieldValue) => {
        if (fieldValue === null || fieldValue === undefined) {
            return fieldValue;
        }
        // If it's an object with a 'value' property, extract it recursively
        if (typeof fieldValue === 'object' && !Array.isArray(fieldValue) && fieldValue.hasOwnProperty('value')) {
            return extractValue(fieldValue.value);
        }
        // Otherwise return the value as-is
        return fieldValue;
    }

    // Process fields to extract values from dynamic field objects
    const processedFields = currentNode.data.fields ? currentNode.data.fields.map(field => {
        // Create a new field object with extracted value
        const processedField = { ...field };
        if (field.value !== undefined && field.value !== null) {
            processedField.value = extractValue(field.value);
        }
        return processedField;
    }) : currentNode.data.fields;

    var djangoData = {
        operation: 'createTrigger',
        data: {
            workflow_id: state.id,
            start_node: state.currentElementID ?? currentNode.id,
            triggerApp: currentNode.data.app,
            triggerResource: triggerResourceValue,
            fields: processedFields,
            credential: currentNode.data.credential,
            pollingParams: currentNode.data.pollingParams,
            url: currentNode.data.url
        }
    }

   

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }

    const resp = await axios(config)
    return {
        nodeID: currentNode.id,
        triggerMessage: resp.data
    }
})

export const deleteTrigger = createAsyncThunk('workflow/deleteTrigger', async (djangoId, { getState }) => {
    const state = getState().workflow.present

    let currentNode = state.elements.find(item => item.type == 'trigger')
    
    if (currentNode.data.type === 'webhook' && currentNode.data.app !== 'schedule' && currentNode.data.app !== 'webhook' && currentNode.data.unsubscribe) {
    const webhook_obj = currentNode.data.triggerMessage
    let id = ''
    if (webhook_obj.data) {
        id = webhook_obj.data.id
    }
    else {
         id = webhook_obj.id
    }
  
     
    
   
    var djangoData = {
        operation: 'getDD',
        data: {
            url: currentNode.data.url,
            app: currentNode.data.app,
            credential: currentNode.data.credential,
            action: 'deleteWebhook',
            fields: [{"name": "id", "in": "path", "value": id}]
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }

    const resp = await axios(config)
    return {
        nodeID: currentNode.id,
        triggerMessage: resp.data
    }
} else {
    var djangoData = {
        operation: 'createSchedule',
        data: {
            webhookURL: state.id,
            scheduleType: 'stopPolling',

        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }

    const resp = await axios(config)
    return {
        nodeID: currentNode.id,
        triggerMessage: "",
        djangoResponse: resp.data
    }
}
})

export const fetchTriggerChoices = createAsyncThunk('workflow/fetchTriggerChoices', async (options = {}, { getState }) => {
    const state = getState().workflow.present
    // Handle both old style (number) and new style (object) calls
    let skip = 0, limit = 5, append = false
    let nodeIdOverride = null
    if (typeof options === 'number') {
        // Old style: just use defaults
        skip = 0
        limit = 5
        append = false
    } else if (typeof options === 'object' && options !== null) {
        skip = options.skip || 0
        limit = options.limit || 5
        append = options.append || false
        nodeIdOverride = options.nodeId ?? null
    }

    // When nodeId is passed (e.g. from AI runTestStep), use that node without changing selected element
    const targetId = nodeIdOverride != null ? nodeIdOverride : state.currentElementID
    var currentNode = state.elements.find(item => item.id == targetId)
    if (!currentNode || !currentNode.data) {
        throw new Error(`Trigger node not found: ${targetId}`)
    }
    // Built-in apps (schedule, webhook, workload-forms) do not use getDD for trigger choices;
    // calling getDD with performList would cause 500. Return empty choices so test step can use triggerMessage/recent data.
    if (isBuiltInTriggerApp(currentNode.data.app)) {
        return {
            nodeID: targetId,
            triggerChoices: [],
            append: false,
            hasMore: false
        }
    }

    // Resolve url for getDD when node has app but no url (e.g. AI-applied workflow before panel opened)
    let triggerUrl = currentNode.data.url || '';
    if (!triggerUrl.trim() && currentNode.data.app) {
        try {
            const appsResp = await axios.get('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/apps/', {
                headers: { 'Content-Type': 'application/json', 'x-api-key': 'iJFqQQXezM4GAMt1r1bvB51Rbah3GyQsahdG5ePC' },
            });
            const apps = Array.isArray(appsResp.data) ? appsResp.data : (appsResp.data?.apps || appsResp.data?.data || []);
            const appMeta = apps.find(
                (a) =>
                    (a.app && a.app.toLowerCase() === String(currentNode.data.app).toLowerCase()) ||
                    (a.slug && a.slug.toLowerCase() === String(currentNode.data.app).toLowerCase()) ||
                    (a.passportApp && a.passportApp.toLowerCase() === String(currentNode.data.app).toLowerCase())
            );
            triggerUrl = appMeta?.formDataEndpoint || appMeta?.url || '';
        } catch (e) {
            console.warn('fetchTriggerChoices: could not resolve formDataEndpoint for app', currentNode.data.app, e);
        }
    }
    if (!triggerUrl.trim()) {
        throw new Error(`Trigger node has no URL for getDD. App: ${currentNode.data.app}. Open the trigger and select the app, or ensure the workflow has a formDataEndpoint for this app.`);
    }

    let replaced_params = []
    const result = currentNode.data.fields.filter(x => x.schema.type !== 'object');
    const result_body = currentNode.data.fields.filter(x => x.schema.type === 'object')

    // Helper function to extract primitive value from potentially nested {label, value} objects
    const extractValue = (fieldValue) => {
        if (fieldValue === null || fieldValue === undefined) {
            return fieldValue;
        }
        // If it's an object with a 'value' property, extract it recursively
        if (typeof fieldValue === 'object' && !Array.isArray(fieldValue) && fieldValue.hasOwnProperty('value')) {
            return extractValue(fieldValue.value);
        }
        // Otherwise return the value as-is
        return fieldValue;
    }

       if (result.length > 0) {
        result.forEach(result => {
            const extractedValue = extractValue(result.value);
            replaced_params.push({'name':result.name,'in':result.in,'value': extractedValue})
        })
       }
       if (result_body.length > 0) {
      var keys = Object.keys(result_body[0].value)
      keys.forEach(element => {
          const extractedValue = extractValue(result_body[0].value[element]);
          replaced_params.push({'name':element,'in':'path','value': extractedValue})
      })
  


        }
       
    
    var djangoData = {
        operation: 'getDD',
        data: {
            url: triggerUrl,
            app: currentNode.data.app,
            credential: currentNode.data.credential,
            action: currentNode.data.performList,
            fields: replaced_params
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        //timeout: 40000,
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }
    let choices = []
    const resp = await axios(config)
    function extractArray(apiResponse) {
        // Check if the response is an array
        if (Array.isArray(apiResponse)) {
            return apiResponse;
        }
    
        // If the response is an object, iterate through its keys
        for (const key in apiResponse) {
            if (Array.isArray(apiResponse[key])) {
                return apiResponse[key];
            }
        }
    
        // Return an empty array if no array is found
        return [];
    }
    const obj = resp.data
    choices = extractArray(obj)
   /*  const check_obj = Object.values(resp.data)
    if (Array.isArray(check_obj) === true && !currentNode.data.pollingParams) {
         choices = check_obj

         
     }
      else if (Object.keys(currentNode.data.pollingParams).length > 0){
        if (currentNode.data.pollingParams.data_head === '0'){
            choices = obj
        } else if (currentNode.data.pollingParams.data_head) {
        choices = obj[currentNode.data.pollingParams.data_head]
        } else {
    const obj_values = Object.values(obj)
    const obj_values_array_filter = obj_values.filter(x => Array.isArray(x) === true)
    //if obj_values_array_filter is still empty, one last shot to find an array of data:
    
    if (obj_values_array_filter.length === 0) {
         let results = []
        for (let key in obj.data) {
            if (typeof obj.data[key] === "object") {
              for (let nestedKey in obj.data[key]) {
                results.push(obj.data[key][nestedKey])
              }
            } else {
             
            }
          }
          choices = results
    } else {
        choices = obj_values_array_filter[0]
    }
   
    }
} else {
    const obj_values = Object.values(obj)
  
 if (Array.isArray(obj_values) === true) {
    
        choices = obj_values[0]
    }
     else {
     const obj_values_array_filter = obj_values.filter(x => Array.isArray(x) === true)
     choices = obj_values_array_filter[0]
     }
}  */

    // Apply pagination: skip the first 'skip' items and take 'limit' items
    const paginatedChoices = choices.slice(skip, skip + limit)
    
    return {
        nodeID: targetId,
        triggerChoices: paginatedChoices,
        append: append,
        hasMore: choices.length > skip + limit // Indicate if there are more items available
    }
})

export const createWorkflow = createAsyncThunk('workflow/createWorkflow', async (scaffoldID, { getState }) => {
    const state = getState().workflow.present
    const initElement = [
        {
            "id": "b9186a86-827e-431b-b34a-448cbfb4763d",
            "draggable": false,
            "data": {
                "name": "Add trigger",

                "type": "trigger",
            },
            "type": "trigger",
            "position": {
                "x": 220,
                "y": 40
            }
        },
        {
            "id": "26879b45-3bce-4614-bf9f-a7b46fa11bbd",
            "data": {
                "name": "Add action"
            },
            "type": "action",
            "position": {
                "x": 220,
                "y": 240
            }
        },
        {
            "id": "f2833d26-430a-4175-98ec-3eba1959f8e8",
            "data": {
                "text": "empty",
                "fields": filterFields
            },
            "type": "filter",
            "style": {
                "stroke": "#0069ea",
                "strokeWidth": 2
            },
            "source": "b9186a86-827e-431b-b34a-448cbfb4763d",
            "target": "26879b45-3bce-4614-bf9f-a7b46fa11bbd",
            "animated": false
        }
    ]
    const initBackendNodes = [
        {
            "26879b45-3bce-4614-bf9f-a7b46fa11bbd": {
                "id": "26879b45-3bce-4614-bf9f-a7b46fa11bbd",
                "name": "Add action",
                "renderInfo": {
                    "x": 220,
                    "y": 240
                },
                "connections": {},
                "delay": 0,
                "inputFields": {},
                "outputFields": {}
            },
            "b9186a86-827e-431b-b34a-448cbfb4763d": {
                "id": "b9186a86-827e-431b-b34a-448cbfb4763d",
                "name": "Choose a Trigger",
                "renderInfo": {
                    "x": 220,
                    "y": 20
                },
                "connections": {
                    "26879b45-3bce-4614-bf9f-a7b46fa11bbd": [
                        [
                            {
                                "conditionType": "default"
                            }
                        ]
                    ]
                },
                "delay": 0,
                "inputFields": {},
                "outputFields": {}
            }
        }
    ]

    var djangoData = {
        operation: 'createWorkflow',
        data: {
            name: "Untitled Workflow (Draft)",
            active: false,
            current_state: {
                frontendWorkflow: initElement,
                workflowTestbed: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} },
                workflowTemplate: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} }
            }
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }

    const resp = await axios(config).catch(e => {})
    const wfID = resp.data.id
    config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap',
        headers: {
            'content-type': 'application/json'
        },
        data: {
            scaffoldId: scaffoldID,
            djangoId: wfID
        }
    }
    const resp2 = await axios(config).catch(e => {})
    //window.location.replace("https://editor.workload.co/?id=" + scaffoldID)
    return {
        initialElement: initElement,
        response: resp.data
    }
})

const DJANGOCALLER_URL = 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller';

/**
 * Build a workflow primitive (frontend elements) from an agent: one trigger node + one action node per agent.actions, with edges trigger -> each action.
 * Used to persist agent as a workflow with type "agent" so createTrigger/webhooks have a real workflow_id.
 */
export function buildAgentWorkflowPrimitive(agent) {
    const trigger = (agent?.triggers || [])[0];
    const actions = agent?.actions || [];
    const elements = [];
    const triggerId = trigger?.trigger_id || `trigger_${Date.now()}_${uuidv4().slice(0, 6)}`;
    const triggerKey = trigger?.trigger_key || trigger?.type || '';
    const eventType = trigger?.event_type || '';
    const performList = resolveAgentTriggerPerformList(trigger);
    const isSchedule = trigger?.app === 'schedule';
    const nodeDataType = isSchedule
        ? 'webhook'
        : eventType === 'polling'
            ? 'polling'
            : eventType === 'webhook'
                ? 'webhook'
                : (trigger?.pollingParams ? 'polling' : 'webhook');
    const triggerNode = {
        id: triggerId,
        draggable: false,
        type: 'trigger',
        data: {
            name: trigger?.trigger_label || trigger?.app_label || 'Trigger',
            type: nodeDataType,
            app: trigger?.app || '',
            action: triggerKey,
            trigger_key: triggerKey,
            triggerKey: triggerKey,
            triggerResource: nodeDataType === 'polling' ? performList : (trigger?.triggerResource || triggerKey),
            performList: nodeDataType === 'polling' ? performList : '',
            url: trigger?.url || '',
            fields: Array.isArray(trigger?.fields) ? trigger.fields : [],
            credential: trigger?.credential || '',
            pollingParams: trigger?.pollingParams || (nodeDataType === 'polling' ? {} : undefined),
            // Needed for Forms retrieveForm → webhook_url (createTrigger result).
            ...(trigger?.triggerMessage != null ? { triggerMessage: trigger.triggerMessage } : {}),
        },
        position: { x: 220, y: 40 },
    };
    elements.push(triggerNode);
    const actionNodes = [];
    actions.forEach((a, i) => {
        const actionId = a?.action_id || `action_${Date.now()}_${i}_${uuidv4().slice(0, 6)}`;
        actionNodes.push({
            id: actionId,
            type: 'action',
            data: {
                name: a?.label || a?.action_label || a?.app_label || 'Action',
                type: a?.app || 'action',
                app: a?.app || '',
                action: a?.config?.action || a?.action_key || '',
                url: a?.url || '',
                fields: Array.isArray(a?.config?.fields) ? a.config.fields : (Array.isArray(a?.fields) ? a.fields : []),
                credential: a?.credential || a?.config?.credential || '',
            },
            position: { x: 220, y: 240 + i * 120 },
        });
    });
    elements.push(...actionNodes);
    actionNodes.forEach((actionNode) => {
        elements.push({
            id: `e_${triggerId}_${actionNode.id}`,
            source: triggerId,
            target: actionNode.id,
            data: { text: 'empty', fields: filterFields },
        });
    });
    return elements;
}

/**
 * Create a workflow with type "agent" and primitive frontendWorkflow from agent. Returns workflow id.
 * Does not call scaffolddjangomap. Used so agent has a real workflow_id for createTrigger.
 */
export const createWorkflowForAgent = createAsyncThunk('workflow/createWorkflowForAgent', async (agent, { getState }) => {
    let elements = buildAgentWorkflowPrimitive(agent);
    elements = ensureNodeKeysForAgent(elements);
    elements = normalizeBuiltInAppUrlsInElements(elements);
    elements = scrubBodyValuesInElements(elements);
    const backendNodes = convertToBackendNodes(elements);
    const djangoData = {
        operation: 'createWorkflow',
        data: {
            type: 'agent',
            name: agent?.name || 'Untitled Agent',
            active: false,
            current_state: {
                frontendWorkflow: elements,
                workflowTestbed: { nodesRun: 0, flowControl: [], nodes: backendNodes, globals: {} },
                workflowTemplate: { nodesRun: 0, flowControl: [], nodes: backendNodes, globals: {} },
            },
        },
    };
    const resp = await axios({
        method: 'post',
        url: DJANGOCALLER_URL,
        headers: { 'Content-Type': 'application/json' },
        data: djangoData,
    });
    return resp.data?.id ?? resp.data;
});

/**
 * Update an existing workflow (type "agent") with primitive built from agent.
 */
export const updateWorkflowForAgent = createAsyncThunk('workflow/updateWorkflowForAgent', async ({ workflowId, agent }, { getState }) => {
    let elements = buildAgentWorkflowPrimitive(agent);
    elements = ensureNodeKeysForAgent(elements);
    elements = normalizeBuiltInAppUrlsInElements(elements);
    elements = scrubBodyValuesInElements(elements);
    const backendNodes = convertToBackendNodes(elements);
    const djangoData = {
        operation: 'updateWorkflow',
        id: workflowId,
        data: {
            type: 'agent',
            name: agent?.name || 'Untitled Agent',
            current_state: {
                frontendWorkflow: elements,
                workflowTestbed: { nodesRun: 0, flowControl: [], nodes: backendNodes, globals: {} },
                workflowTemplate: { nodesRun: 0, active: false, flowControl: [], nodes: backendNodes, globals: {} },
            },
        },
    };
    await axios({
        method: 'post',
        url: DJANGOCALLER_URL,
        headers: { 'Content-Type': 'application/json' },
        data: djangoData,
    });
    return workflowId;
});

/**
 * Create a workflow with type "agent" for an agent (standalone call, no Redux). Returns workflow id.
 * Used from agentApi when creating/updating agents so createTrigger has a real workflow_id.
 */
export async function createWorkflowForAgentApi(agent) {
    let elements = buildAgentWorkflowPrimitive(agent);
    elements = ensureNodeKeysForAgent(elements);
    elements = normalizeBuiltInAppUrlsInElements(elements);
    elements = scrubBodyValuesInElements(elements);
    const backendNodes = convertToBackendNodes(elements);
    const resp = await axios({
        method: 'post',
        url: DJANGOCALLER_URL,
        headers: { 'Content-Type': 'application/json' },
        data: {
            operation: 'createWorkflow',
            data: {
                type: 'agent',
                name: agent?.name || 'Untitled Agent',
                active: false,
                current_state: {
                    frontendWorkflow: elements,
                    workflowTestbed: { nodesRun: 0, flowControl: [], nodes: backendNodes, globals: {} },
                    workflowTemplate: { nodesRun: 0, flowControl: [], nodes: backendNodes, globals: {} },
                },
            },
        },
    });
    return resp.data?.id ?? resp.data;
}

/**
 * Update an existing workflow (type "agent") with primitive from agent (standalone call, no Redux).
 */
export async function updateWorkflowForAgentApi(workflowId, agent, setActive = false) {
    let elements = buildAgentWorkflowPrimitive(agent);
    elements = ensureNodeKeysForAgent(elements);
    elements = normalizeBuiltInAppUrlsInElements(elements);
    elements = scrubBodyValuesInElements(elements);
    const backendNodes = convertToBackendNodes(elements);
    await axios({
        method: 'post',
        url: DJANGOCALLER_URL,
        headers: { 'Content-Type': 'application/json' },
        data: {
            operation: 'updateWorkflow',
            id: workflowId,
            data: {
                type: 'agent',
                name: agent?.name || 'Untitled Agent',
                current_state: {
                    frontendWorkflow: elements,
                    workflowTestbed: { nodesRun: 0, flowControl: [], nodes: backendNodes, globals: {} },
                    workflowTemplate: { nodesRun: 0, active: setActive === true, flowControl: [], nodes: backendNodes, globals: {} },
                },
            },
        },
    });
    return workflowId;
}

// Helper function to sync workflow to scaffoldhub with tenant selection support
const syncWorkflowToScaffoldhub = async (djangoId, scaffoldId, workflowName, token) => {
    try {
        // Get user info to check tenants
        const userResponse = await axios.get('https://backend.workload.co/api/auth/me', { 
            headers: {'Authorization': 'Bearer '+token} 
        });
        
        const tenants = userResponse.data.tenants || [];
        const currentUser = userResponse.data.id;
        const userName = userResponse.data.name;
        
        // If user has multiple tenants, return tenant list for selection
        if (tenants.length > 1) {
            return {
                needsTenantSelection: true,
                tenants: tenants.map(t => ({
                    id: t.tenant.id,
                    name: t.tenant.name || `Tenant ${t.tenant.id}`
                })),
                djangoId: djangoId,
                scaffoldId: scaffoldId,
                workflowName: workflowName || userName,
                token: token,
                currentUser: currentUser
            };
        }
        
        // If only one tenant, proceed directly
        const currentTenant = tenants[0].tenant.id;
        const create_scaffold_workflow = JSON.stringify({ 
            "data": { 
                "name": workflowName || "Untitled Workflow (Draft)", 
                "active": false, 
                "icons": [] 
            } 
        });
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+token
        };
        
        const scaffoldWorkflowResponse = await axios.post('https://backend.workload.co/api/tenant/' + currentTenant + '/workflow', create_scaffold_workflow, {
            headers: headers
        });
        
        const newScaffoldID = scaffoldWorkflowResponse.data.id;
        const scaffoldmap = { 
            scaffoldId: newScaffoldID,
            djangoId: djangoId
        };
        
        await axios.post('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap', scaffoldmap);
        
        return {
            needsTenantSelection: false,
            scaffoldID: newScaffoldID,
            tenant: currentTenant,
            user: currentUser
        };
    } catch (error) {
        console.error('Error syncing workflow to scaffoldhub:', error);
        throw error;
    }
};

export const acceptTemplateNew = createAsyncThunk('workflow/acceptTemplateNew', async (template, { getState, rejectWithValue }) => {
    try {
        // Check if token exists in localStorage
        const token = localStorage.getItem('tok_');
        if (!token) {
            throw new Error('No authentication token found');
        }

        // Get workflow data
        const scaffoldResponse = await axios.get('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap?scaffoldId='+template.template_id);
        const djangoId = scaffoldResponse.data[0].djangoId;
        
        const djangoData = {
            operation: 'getWorkflow',
            id: djangoId
        };
        
        const workflowResponse = await axios.post('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller', djangoData);
        const workflow_data = workflowResponse.data;
        
        const new_name = {'name': 'Template: '+workflow_data.name};
        const frontend_arr = workflow_data.current_state.frontendWorkflow;
        
        frontend_arr.map((item) => {
            delete item.data.credential;
            delete item.data.authname;
            delete item.data.triggerMessage;
            const testData = item.data.testData;
            
            if (testData) {
                Object.keys(testData).forEach(key => {
                    testData[key] = '';
                });
            }
        });
        
        Object.assign(workflow_data, new_name);
        Object.assign(workflow_data.current_state.workflowTemplate, {'active': false});
        Object.assign(workflow_data.current_state.frontendWorkflow, frontend_arr);
        
        const workflowData = {
            operation: 'createWorkflow',
            data: workflow_data
        };
        
        const createWorkflowResponse = await axios.post('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller', workflowData);
        const django_id = createWorkflowResponse.data.id;
        
        // Sync to scaffoldhub with tenant selection support
        const syncResult = await syncWorkflowToScaffoldhub(django_id, template.scaffoldId, workflow_data.name, token);
        
        if (syncResult.needsTenantSelection) {
            return syncResult;
        }
        
        // Keep flag set during redirect
        window.location.replace("https://editor.workload.co/?u="+syncResult.user+"&t="+syncResult.tenant+"&id=" + syncResult.scaffoldID);
        
        return {
            needsTenantSelection: false,
            scaffoldID: syncResult.scaffoldID,
            tenant: syncResult.tenant,
            user: syncResult.user
        };
    } catch (error) {
        console.error('Error accepting template:', error);
        window.location.replace('https://automate.workload.co');
        return rejectWithValue({ error: error.message });
    }
})


export const acceptTemplateExist = createAsyncThunk('workflow/acceptTemplateExist', async (template, { getState, rejectWithValue }) => {
    try {
        // Check if token exists in localStorage
        const token = template.token || localStorage.getItem('tok_');
        if (!token) {
            throw new Error('No authentication token found');
        }

        // Get workflow data
        const scaffoldResponse = await axios.get('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap?scaffoldId='+template.template_id);
        const djangoId = scaffoldResponse.data[0].djangoId;
        
        const djangoData = {
            operation: 'getWorkflow',
            id: djangoId
        };
        
        const workflowResponse = await axios.post('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller', djangoData);
        const workflow_data = workflowResponse.data;
        
        const new_name = {'name': 'Template: '+workflow_data.name};
        const frontend_arr = workflow_data.current_state.frontendWorkflow;
        
        frontend_arr.map((item) => {
            delete item.data.credential;
            delete item.data.authname;
            delete item.data.triggerMessage;
            const testData = item.data.testData;
            
            if (testData) {
                Object.keys(testData).forEach(key => {
                    testData[key] = '';
                });
            }
        });
        
        Object.assign(workflow_data, new_name);
        Object.assign(workflow_data.current_state.workflowTemplate, {'active': false});
        Object.assign(workflow_data.current_state.frontendWorkflow, frontend_arr);
        
        const workflowData = {
            operation: 'createWorkflow',
            data: workflow_data
        };
        
        const createWorkflowResponse = await axios.post('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller', workflowData);
        const django_id = createWorkflowResponse.data.id;
        
        // Get user info to check tenants
        const userResponse = await axios.get('https://backend.workload.co/api/auth/me', { 
            headers: {'Authorization': 'Bearer '+token} 
        });
        
        const tenants = userResponse.data.tenants || [];
        const currentUser = userResponse.data.id;
        const userName = userResponse.data.name;
        
        // If user has multiple tenants, return tenant list for selection
        if (tenants.length > 1) {
            return {
                needsTenantSelection: true,
                tenants: tenants.map(t => ({
                    id: t.tenant.id,
                    name: t.tenant.name || `Tenant ${t.tenant.id}`
                })),
                djangoId: django_id,
                userName: userName,
                token: token,
                currentUser: currentUser
            };
        }
        
        // If only one tenant, proceed directly
        const currentTenant = tenants[0].tenant.id;
        const create_scaffold_workflow = JSON.stringify({ "data": { "name": userName, "active": false, "icons": [] } });
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+token
        };
        
        const scaffoldWorkflowResponse = await axios.post('https://backend.workload.co/api/tenant/' + currentTenant + '/workflow', create_scaffold_workflow, {
            headers: headers
        });
        
        const scaffoldID = scaffoldWorkflowResponse.data.id;
        const scaffoldmap = { 
            scaffoldId: scaffoldID,
            djangoId: django_id
        };
        
        await axios.post('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap', scaffoldmap);
        
        // Keep flag set during redirect
        window.location.replace("https://editor.workload.co/?u="+currentUser+"&t="+currentTenant+"&id=" + scaffoldID);
        
        return {
            needsTenantSelection: false,
            scaffoldID: scaffoldID,
            tenant: currentTenant,
            user: currentUser
        };
    } catch (error) {
        console.error('Error accepting template:', error);
        window.location.replace('https://automate.workload.co');
        return rejectWithValue({ error: error.message });
    }
})

export const completeTemplateAccept = createAsyncThunk('workflow/completeTemplateAccept', async ({ selectedTenant, djangoId, userName, token, currentUser }, { rejectWithValue }) => {
    try {
        // Set flag to disable beforeunload warning during template acceptance
        localStorage.setItem('templateAcceptanceInProgress', 'true');
        
        const create_scaffold_workflow = JSON.stringify({ "data": { "name": userName, "active": false, "icons": [] } });
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+token
        };
        
        const scaffoldWorkflowResponse = await axios.post('https://backend.workload.co/api/tenant/' + selectedTenant + '/workflow', create_scaffold_workflow, {
            headers: headers
        });
        
        const scaffoldID = scaffoldWorkflowResponse.data.id;
        const scaffoldmap = { 
            scaffoldId: scaffoldID,
            djangoId: djangoId
        };
        
        await axios.post('https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap', scaffoldmap);
        
        window.location.replace("https://editor.workload.co/?u="+currentUser+"&t="+selectedTenant+"&id=" + scaffoldID);
        
        return {
            scaffoldID: scaffoldID,
            tenant: selectedTenant,
            user: currentUser
        };
    } catch (error) {
        console.error('Error completing template accept:', error);
        localStorage.removeItem('templateAcceptanceInProgress');
        window.location.replace('https://automate.workload.co');
        return rejectWithValue({ error: error.message });
    }
})

export const createTemplate0 = createAsyncThunk('workflow/createTemplate0', async (template, { getState, rejectWithValue }) => {
    const state = getState().workflow.present


    var getapp = {
        method: 'get',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/apps',
        headers: {
            'Content-Type': 'application/json'
        }
    }

    const allapps = await axios(getapp).catch(e => {})
    const filterIt = (data, searchKey) => data.filter(obj => Object.values(obj).includes(searchKey));
    const newdata = filterIt(allapps.data, template.app);
    var filternode = newdata[0];

    const initElement = [
        {
            "id": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
            "data": {
                "name": "Add trigger"
            },
            "type": "trigger",
            "draggable": false,
            "position": {
                "x": 200,
                "y": 40
            }
        },
        {
            "id": "4199edb1-0808-4944-b3d8-506d29e23783",
            "data": {
                "icon": filternode.icon,
                "name": filternode.name + " - " + template.action,
                "type": template.app,
                "label": filternode.name,
                "hasauth": filternode.hasauth,
                "url": filternode.formDataEndpoint

            },
            "type": "action",
            "position": {
                "x": 200,
                "y": 200
            }
        },
        {
            "id": "reactflow__edge-ca10ec9f-dff8-475a-8cc8-ac4f74938bb9a-4199edb1-0808-4944-b3d8-506d29e23783undefined",
            "data": {
                "text": "empty",
                "fields": filterFields
            },
            "type": "filter",
            "style": {
                "stroke": "#0069ea",
                "strokeWidth": 2
            },
            "source": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
            "target": "4199edb1-0808-4944-b3d8-506d29e23783",
            "animated": false,
            "sourceHandle": "a"
        }
    ]

    const initBackendNodes = [{
        "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9": {
            "id": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
            "name": "Add trigger",
            "inputFields": {},
            "outputFields": {},
            "delay": 0,
            "connections": {
                "4199edb1-0808-4944-b3d8-506d29e23783": [
                    [
                        {
                            "conditionType": "default"
                        }
                    ]
                ]
            }
        },
        "4199edb1-0808-4944-b3d8-506d29e23783": {
            "id": "4199edb1-0808-4944-b3d8-506d29e23783",
            "name": "Add action",
            "type": "action",
            "inputFields": {},
            "outputFields": {},
            "connections": {},
            "delay": 0
        }
    }]

    var djangoData = {
        operation: 'createWorkflow',
        data: {
            name: filternode.name + " -> Workflow (Draft) ",
            active: false,
            current_state: {
                frontendWorkflow: initElement,
                workflowTestbed: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} },
                workflowTemplate: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} }
            }
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }

    const resp = await axios(config).catch(e => {})
    const wfID = resp.data.id
    config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap',
        headers: {
            'content-type': 'application/json'
        },
        data: {
            scaffoldId: template.id,
            djangoId: wfID
        }
    }


    const resp2 = await axios(config).catch(e => {})
    
    // Check if token exists
    const token = template.token || localStorage.getItem('tok_');
    if (!token) {
        // No token - redirect to signup
        const currentUrl = window.location.href;
        window.location.replace(`https://automate.workload.co/auth/signup?next=${encodeURIComponent(currentUrl)}`);
        return;
    }
    
    // Sync to scaffoldhub with tenant selection support
    try {
        const workflowName = filternode.name + " -> Workflow (Draft)";
        const syncResult = await syncWorkflowToScaffoldhub(wfID, template.id, workflowName, token);
        
        if (syncResult.needsTenantSelection) {
            return syncResult;
        }
        
        // Keep flag set during redirect
        window.location.replace("https://editor.workload.co/?u="+syncResult.user+"&t="+syncResult.tenant+"&id=" + syncResult.scaffoldID);
        
        return {
            needsTenantSelection: false,
            scaffoldID: syncResult.scaffoldID,
            tenant: syncResult.tenant,
            user: syncResult.user
        };
    } catch (error) {
        console.error('Error syncing template to scaffoldhub:', error);
        window.location.replace('https://automate.workload.co');
        return rejectWithValue({ error: error.message });
    }
})

export const createTemplate1 = createAsyncThunk('workflow/createTemplate1', async (template, { getState, rejectWithValue }) => {
    const state = getState().workflow.present

    var getapp = {
        method: 'get',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/apps',
        headers: {
            'Content-Type': 'application/json'
        }
    }

    const allapps = await axios(getapp).catch(e => {})
    const filterIt = (data, searchKey) => data.filter(obj => Object.values(obj).includes(searchKey));
    const newdata = filterIt(allapps.data, template.app);
    var filternode = newdata[0];

    var getaction = {
        method: 'get',
        url: filternode.formDataEndpoint,
        headers: {
            'Content-Type': 'application/json'
        }
    }

    const oneaction = await axios(getaction).catch(e => {})
    var list = oneaction.data.actions
    var list_obj = Object.entries(list);
    const filterAction = (data, searchKey) => data.filter(obj => Object.values(obj).includes(searchKey));
    const newaction = filterAction(list_obj, template.action);
    var filteraction = newaction[0];
    const initElement = [{
        "id": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
        "data": {
            "name": "Add trigger"
        },
        "type": "trigger",
        "draggable": false,
        "position": {
            "x": 200,
            "y": 40
        }
    },
    {
        "id": "4199edb1-0808-4944-b3d8-506d29e23783",
        "data": {
            "icon": filternode.icon,
            "name": filternode.name + " - " + template.action,
            "type": template.app,
            "label": filternode.name,
            "hasauth": filternode.hasauth,
            "url": filternode.formDataEndpoint,
            "action": filteraction[0],
            "fields": filteraction[1].fields
        },
        "type": "action",
        "position": {
            "x": 200,
            "y": 200
        }
    },
    {
        "id": "reactflow__edge-ca10ec9f-dff8-475a-8cc8-ac4f74938bb9a-4199edb1-0808-4944-b3d8-506d29e23783undefined",
        "data": {
            "text": "empty",
            "fields": filterFields
        },
        "type": "filter",
        "style": {
            "stroke": "#0069ea",
            "strokeWidth": 2
        },
        "source": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
        "target": "4199edb1-0808-4944-b3d8-506d29e23783",
        "animated": false,
        "sourceHandle": "a"
    }
    ]

    const initBackendNodes = [{
        "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9": {
            "id": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
            "name": "Add trigger",
            "inputFields": {},
            "outputFields": {},
            "delay": 0,
            "connections": {
                "4199edb1-0808-4944-b3d8-506d29e23783": [
                    [
                        {
                            "conditionType": "default"
                        }
                    ]
                ]
            }
        },
        "4199edb1-0808-4944-b3d8-506d29e23783": {
            "id": "4199edb1-0808-4944-b3d8-506d29e23783",
            "name": "Add action",
            "type": "lever",
            "inputFields": {},
            "outputFields": {},
            "connections": {},
            "delay": 0
        }
    }]

    var djangoData = {
        operation: 'createWorkflow',
        data: {
            name: filternode.name + " -> " + template.action,
            active: false,
            current_state: {
                frontendWorkflow: initElement,
                workflowTestbed: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} },
                workflowTemplate: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} }
            }
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }

    const resp = await axios(config).catch(e => {})
    const wfID = resp.data.id
    config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap',
        headers: {
            'content-type': 'application/json'
        },
        data: {
            scaffoldId: template.id,
            djangoId: wfID
        }
    }


    const resp2 = await axios(config).catch(e => {})
    
    // Check if token exists
    const token = template.token || localStorage.getItem('tok_');
    if (!token) {
        // No token - redirect to signup
        const currentUrl = window.location.href;
        window.location.replace(`https://automate.workload.co/auth/signup?next=${encodeURIComponent(currentUrl)}`);
        return;
    }
    
    // Sync to scaffoldhub with tenant selection support
    try {
        const workflowName = filternode.name + " -> " + template.action;
        const syncResult = await syncWorkflowToScaffoldhub(wfID, template.id, workflowName, token);
        
        if (syncResult.needsTenantSelection) {
            return syncResult;
        }
        
        // Keep flag set during redirect
        window.location.replace("https://editor.workload.co/?u="+syncResult.user+"&t="+syncResult.tenant+"&id=" + syncResult.scaffoldID);
        
        return {
            needsTenantSelection: false,
            scaffoldID: syncResult.scaffoldID,
            tenant: syncResult.tenant,
            user: syncResult.user
        };
    } catch (error) {
        console.error('Error syncing template to scaffoldhub:', error);
        window.location.replace('https://automate.workload.co');
        return rejectWithValue({ error: error.message });
    }
})

export const createTemplate2 = createAsyncThunk('workflow/createTemplate2', async (template, { getState, rejectWithValue }) => {
    const state = getState().workflow.present

    var getapp = {
        method: 'get',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/apps',
        headers: {
            'Content-Type': 'application/json'
        }
    }

    const allapps = await axios(getapp).catch(e => {})
    const filterIt = (data, searchKey) => data.filter(obj => Object.values(obj).includes(searchKey));
    const newdata = filterIt(allapps.data, template.app1);
    var filternode = newdata[0];
    const newdata2 = filterIt(allapps.data, template.app2);
    var empty_state = {app: '', formDataEndpoint: '', icon: '', name: '', auth: ''}
    var filternode2 = template.app2 === 'null' ? empty_state : newdata2[0];


    const initElement = [{
    "id": "b9186a86-827e-431b-b34a-448cbfb4763d",
    "data": {
        "app": filternode.app,
        "url": filternode.formDataEndpoint,
        "icon": filternode.icon,
        "name": "Add trigger",
        "type": "trigger",
        "label": filternode.name,
        "hasauth": filternode.auth,
        "triggerResource": ""
    },
    "type": "trigger",
    "draggable": false,
    "position": {
        "x": 220,
        "y": 40
    }
},
{
    "id": "26879b45-3bce-4614-bf9f-a7b46fa11bbd",
    "data": {
        "url": filternode2.formDataEndpoint,
        "icon": filternode2.icon,
        "name": "Add action",
        "type": filternode2.app,
        "label": filternode2.name,
        "hasauth": filternode2.auth
    },
    "type": "action",
    "position": {
        "x": 220,
        "y": 240
    }
},
{
    "id": "f2833d26-430a-4175-98ec-3eba1959f8e8",
    "data": {
        "text": "empty",
        "fields": filterFields
    },
    "type": "filter",
    "style": {
        "stroke": "#0069ea",
        "strokeWidth": 2
    },
    "source": "b9186a86-827e-431b-b34a-448cbfb4763d",
    "target": "26879b45-3bce-4614-bf9f-a7b46fa11bbd",
    "animated": false
}
    ]

    const initBackendNodes = [{
        "b9186a86-827e-431b-b34a-448cbfb4763d": {
            "id": "b9186a86-827e-431b-b34a-448cbfb4763d",
            "name": filternode.name,
            "type": filternode.app,
            "inputFields": {},
            "outputFields": {},
            "delay": 0,
            "connections": {
                "26879b45-3bce-4614-bf9f-a7b46fa11bbd": [
                    [
                        {
                            "conditionType": "default"
                        }
                    ]
                ]
            }
        },
        "26879b45-3bce-4614-bf9f-a7b46fa11bbd": {
            "id": "26879b45-3bce-4614-bf9f-a7b46fa11bbd",
            "name": filternode2.name,
            "type": filternode2.app,
            "inputFields": {},
            "outputFields": {},
            "connections": {},
            "delay": 0
        }
    }]

    var djangoData = {
        operation: 'createWorkflow',
        data: {
            name: filternode.name + " -> " + filternode2.name,
            active: false,
            current_state: {
                frontendWorkflow: initElement,
                workflowTestbed: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} },
                workflowTemplate: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} }
            }
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }

    const resp = await axios(config).catch(e => {})
    const wfID = resp.data.id
    config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap',
        headers: {
            'content-type': 'application/json'
        },
        data: {
            scaffoldId: template.id,
            djangoId: wfID
        }
    }


    const resp2 = await axios(config).catch(e => {})
    
    // Check if token exists
    const token = template.token || localStorage.getItem('tok_');
    if (!token) {
        // No token - redirect to signup
        const currentUrl = window.location.href;
        window.location.replace(`https://automate.workload.co/auth/signup?next=${encodeURIComponent(currentUrl)}`);
        return;
    }
    
    // Sync to scaffoldhub with tenant selection support
    try {
        const workflowName = filternode.name + " -> " + filternode2.name;
        const syncResult = await syncWorkflowToScaffoldhub(wfID, template.id, workflowName, token);
        
        if (syncResult.needsTenantSelection) {
            return syncResult;
        }
        
        // Keep flag set during redirect
        window.location.replace("https://editor.workload.co/?u="+syncResult.user+"&t="+syncResult.tenant+"&id=" + syncResult.scaffoldID);
        
        return {
            needsTenantSelection: false,
            scaffoldID: syncResult.scaffoldID,
            tenant: syncResult.tenant,
            user: syncResult.user
        };
    } catch (error) {
        console.error('Error syncing template to scaffoldhub:', error);
        window.location.replace('https://automate.workload.co');
        return rejectWithValue({ error: error.message });
    }


})


export const createTemplate4 = createAsyncThunk('workflow/createTemplate4', async (template, { getState, rejectWithValue }) => {
    const state = getState().workflow.present

    var getapp = {
        method: 'get',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/apps',
        headers: {
            'Content-Type': 'application/json'
        }
    }

    const allapps = await axios(getapp).catch(e => {})
    const filterIt = (data, searchKey) => data.filter(obj => Object.values(obj).includes(searchKey));
    const newdata = filterIt(allapps.data, template.app1);
    var filternode = newdata[0];
  const newdata2 = filterIt(allapps.data, template.app2);
  var empty_state = {app: '', formDataEndpoint: '', icon: '', name: '', auth: ''}
  var filternode2 = template.app2 === 'null' ? empty_state : newdata2[0];

    var getaction = {
        method: 'get',
        url: filternode.formDataEndpoint,
        headers: {
            'Content-Type': 'application/json'
        }
    }

    const oneaction = await axios(getaction).catch(e => {})
    var list = oneaction.data.triggers
    var list_obj = Object.entries(list);
    const filterAction = (data, searchKey) => data.filter(obj => Object.values(obj).includes(searchKey));
    const newaction = filterAction(list_obj, template.action1);
    var filteraction = newaction[0];

   var getaction2 = {
    method: 'get',
    url: filternode2.formDataEndpoint,
    headers: {
        'Content-Type': 'application/json'
    }
}

const oneaction2 = await axios(getaction2).catch(e => {})
var list2 = oneaction2.data.actions
var list_obj2 = Object.entries(list2);
const filterAction2 = (data, searchKey) => data.filter(obj => Object.values(obj).includes(searchKey));
   const newaction2 = filterAction2(list_obj2, template.action2);
    var filteraction2 = newaction2[0];
    const initElement = [{
        "id": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
        "data":  {
            "app": filternode.app,
            "url": filternode.formDataEndpoint,
            "icon": filternode.icon,
            "name": "Add trigger",
            "type": "trigger",
            "label": filternode.name,
            "hasauth": filternode.auth,
            "triggerResource": filteraction[0]
        },
        "type": "trigger",
        "draggable": false,
        "position": {
            "x": 200,
            "y": 40
        }
    },
    {
        "id": "4199edb1-0808-4944-b3d8-506d29e23783",
        "data": {
            "icon": filternode2.icon,
            "name": filternode2.name + " - " + template.action,
            "type": template.app2,
            "label": filternode2.name,
            "hasauth": filternode2.hasauth,
            "url": filternode2.formDataEndpoint,
            "action": filteraction2[0],
            "fields": filteraction2[1].fields
        },
        "type": "action",
        "position": {
            "x": 200,
            "y": 200
        }
    },
    {
        "id": "reactflow__edge-ca10ec9f-dff8-475a-8cc8-ac4f74938bb9a-4199edb1-0808-4944-b3d8-506d29e23783undefined",
        "data": {
            "text": "empty",
            "fields": filterFields
        },
        "type": "filter",
        "style": {
            "stroke": "#0069ea",
            "strokeWidth": 2
        },
        "source": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
        "target": "4199edb1-0808-4944-b3d8-506d29e23783",
        "animated": false,
        "sourceHandle": "a"
    }
    ]

    const initBackendNodes = [{
        "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9": {
            "id": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
            "name": "Add trigger",
            "inputFields": {},
            "outputFields": {},
            "delay": 0,
            "connections": {
                "4199edb1-0808-4944-b3d8-506d29e23783": [
                    [
                        {
                            "conditionType": "default"
                        }
                    ]
                ]
            }
        },
        "4199edb1-0808-4944-b3d8-506d29e23783": {
            "id": "4199edb1-0808-4944-b3d8-506d29e23783",
            "name": "Add action",
            "type": "lever",
            "inputFields": {},
            "outputFields": {},
            "connections": {},
            "delay": 0
        }
    }]
    var djangoData = {
        operation: 'createWorkflow',
        data: {
            name: filternode.name + " -> " + template.action,
            active: false,
            current_state: {
                frontendWorkflow: initElement,
                workflowTestbed: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} },
                workflowTemplate: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} }
            }
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }

    const resp = await axios(config).catch(e => {})
    const wfID = resp.data.id
    config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap',
        headers: {
            'content-type': 'application/json'
        },
        data: {
            scaffoldId: template.id,
            djangoId: wfID
        }
    }


    const resp2 = await axios(config).catch(e => {})
    
    // Check if token exists
    const token = template.token || localStorage.getItem('tok_');
    if (!token) {
        // No token - redirect to signup
        const currentUrl = window.location.href;
        window.location.replace(`https://automate.workload.co/auth/signup?next=${encodeURIComponent(currentUrl)}`);
        return;
    }
    
    // Sync to scaffoldhub with tenant selection support
    try {
        const workflowName = filternode.name + " -> " + template.action2;
        const syncResult = await syncWorkflowToScaffoldhub(wfID, template.id, workflowName, token);
        
        if (syncResult.needsTenantSelection) {
            return syncResult;
        }
        
        // Keep flag set during redirect
        window.location.replace("https://editor.workload.co/?u="+syncResult.user+"&t="+syncResult.tenant+"&id=" + syncResult.scaffoldID);
        
        return {
            needsTenantSelection: false,
            scaffoldID: syncResult.scaffoldID,
            tenant: syncResult.tenant,
            user: syncResult.user
        };
    } catch (error) {
        console.error('Error syncing template to scaffoldhub:', error);
        window.location.replace('https://automate.workload.co');
        return rejectWithValue({ error: error.message });
    }

})

export const createTemplate5 = createAsyncThunk('workflow/createTemplate5', async (template, { getState, rejectWithValue }) => {
    const state = getState().workflow.present


    var getapp = {
        method: 'get',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/apps',
        headers: {
            'Content-Type': 'application/json'
        }
    }

    const initElement = [{
        "id": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
        "data": {
            "name": "Add trigger"
        },
        "type": "trigger",
        "draggable": false,
        "position": {
            "x": 200,
            "y": 40
        }
    },
    {
        "id": "4199edb1-0808-4944-b3d8-506d29e23783",
        "data": {
            "name": "Add action"
        },
        "type": "action",
        "position": {
            "x": 200,
            "y": 200
        }
    },
    {
        "id": "reactflow__edge-ca10ec9f-dff8-475a-8cc8-ac4f74938bb9a-4199edb1-0808-4944-b3d8-506d29e23783undefined",
        "data": {
            "text": "empty",
            "fields": filterFields
        },
        "type": "filter",
        "style": {
            "stroke": "#0069ea",
            "strokeWidth": 2
        },
        "source": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
        "target": "4199edb1-0808-4944-b3d8-506d29e23783",
        "animated": false,
        "sourceHandle": "a"
    }
    ]

    const initBackendNodes = [{
        "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9": {
            "id": "ca10ec9f-dff8-475a-8cc8-ac4f74938bb9",
            "name": "Add trigger",
            "inputFields": {},
            "outputFields": {},
            "delay": 0,
            "connections": {
                "4199edb1-0808-4944-b3d8-506d29e23783": [
                    [
                        {
                            "conditionType": "default"
                        }
                    ]
                ]
            }
        },
        "4199edb1-0808-4944-b3d8-506d29e23783": {
            "id": "4199edb1-0808-4944-b3d8-506d29e23783",
            "name": "Add action",
            "type": "lever",
            "inputFields": {},
            "outputFields": {},
            "connections": {},
            "delay": 0
        }
    }]

    var djangoData = {
        operation: 'createWorkflow',
        data: {
            name: "Untitled Workflow (Draft)",
            active: false,
            current_state: {
                frontendWorkflow: initElement,
                workflowTestbed: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} },
                workflowTemplate: { nodesRun: 0, flowControl: [], nodes: initBackendNodes, globals: {} }
            }
        }
    }

    var config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/djangocaller',
        headers: {
            'Content-Type': 'application/json'
        },
        data: djangoData
    }

    const resp = await axios(config).catch(e => {})
    const wfID = resp.data.id
    config = {
        method: 'post',
        url: 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/docdb/scaffolddjangomap',
        headers: {
            'content-type': 'application/json'
        },
        data: {
            scaffoldId: template.id,
            djangoId: wfID
        }
    }


    const resp2 = await axios(config).catch(e => {})
    
    // Check if token exists
    const token = template.token || localStorage.getItem('tok_');
    if (!token) {
        // No token - redirect to signup
        const currentUrl = window.location.href;
        window.location.replace(`https://automate.workload.co/auth/signup?next=${encodeURIComponent(currentUrl)}`);
        return;
    }
    
    // Sync to scaffoldhub with tenant selection support
    try {
        const workflowName = "Untitled Workflow (Draft)";
        const syncResult = await syncWorkflowToScaffoldhub(wfID, template.id, workflowName, token);
        
        if (syncResult.needsTenantSelection) {
            return syncResult;
        }
        
        // Keep flag set during redirect
        window.location.replace("https://editor.workload.co/?u="+syncResult.user+"&t="+syncResult.tenant+"&id=" + syncResult.scaffoldID);
        
        return {
            needsTenantSelection: false,
            scaffoldID: syncResult.scaffoldID,
            tenant: syncResult.tenant,
            user: syncResult.user
        };
    } catch (error) {
        console.error('Error syncing template to scaffoldhub:', error);
        window.location.replace('https://automate.workload.co');
        return rejectWithValue({ error: error.message });
    }
})

export const pollForTrigger = createAsyncThunk('workflow/pollForTrigger', async (nodeID, { getState }) => {

})



const workflowSlice = createSlice({
    name: 'workflow',
    initialState: defaultState,
    reducers: {
        nodeAdded: {
            reducer(state, action) {
                state.elements.push(action.payload)
            },
            prepare(nodeType, x, y) {
                return {
                    payload: {
                        id: uuidv4(),
                        type: nodeType,
                        position: {
                            x: x,
                            y: y
                        },
                        data: {
                            name: 'Add action'
                        }
                    },
                    meta: {
                        description: 'add step'
                      }
                }
            }
        },
        nodeDuplicated: {
            reducer(state, action) {
              // Here action.payload could be an object with keys: newNode, newEdge1, newEdge2, originalEdgeId (optional)
              // Remove the original edge if needed:
              if (action.payload.removeOriginalEdge) {
                state.elements = state.elements.filter(
                  (el) => el.id !== action.payload.originalEdgeId
                );
              }
              // Add the new node and the two new edges:
              state.elements.push(action.payload.newNode);
              state.elements.push(action.payload.newEdge1);
              state.elements.push(action.payload.newEdge2);
            },
            prepare(nodeType, x, y, source, target, removeOriginalEdge = false, originalEdgeId = null, data) {
            const newData = {
                ...data,
                name: `Duplicated: ${data.name}`
            };
              const newNodeId = uuidv4();
              const newNode = {
                id: newNodeId,
                type: nodeType,
                position: { x, y },
                data: newData,
              };
          
              const newEdge1 = {
                id: uuidv4(),
                source: source,
                target: newNodeId,
                type: 'filter',
                animated: false,
                style: { stroke: '#0069ea', strokeWidth: 2 },
                data: { text: 'empty', fields: filterFields },
              };
          
              const newEdge2 = {
                id: uuidv4(),
                source: newNodeId,
                target: target,
                type: 'filter',
                animated: false,
                style: { stroke: '#0069ea', strokeWidth: 2 },
                data: { text: 'empty', fields: filterFields },
              };
          
              return {
                payload: {
                  newNode,
                  newEdge1,
                  newEdge2,
                  removeOriginalEdge,
                  originalEdgeId,
                },
                meta: {
                    description: 'duplicated step'
                  }
              };
            },
          }, 
        edgeAdded: {
            reducer(state, action) {
                if (action.payload.source != action.payload.target) {
                    state.elements.push(action.payload)
                }
            },
            prepare(edgeType, source, target) {
                return {
                    payload: {
                        source: source,
                        target: target,
                        id: uuidv4(),
                        type: edgeType,
                        animated: false,
                        style: {
                            stroke: '#0069ea',
                            strokeWidth: 2
                        },
                        data: {
                            text: 'empty',
                            fields: filterFields
                        }
                    }
                }
            }
        },
        nodeInserted: {
            reducer(state, action) {
              // Here action.payload could be an object with keys: newNode, newEdge1, newEdge2, originalEdgeId (optional)
              // Remove the original edge if needed:
              if (action.payload.removeOriginalEdge) {
                state.elements = state.elements.filter(
                  (el) => el.id !== action.payload.originalEdgeId
                );
              }
              // Add the new node and the two new edges:
              state.elements.push(action.payload.newNode);
              state.elements.push(action.payload.newEdge1);
              state.elements.push(action.payload.newEdge2);
            },
            prepare(nodeType, x, y, source, target, removeOriginalEdge = false, originalEdgeId = null, data = null) {
              const newNodeId = uuidv4();
              const newData = data && typeof data === 'object' ? { ...data } : { name: 'Add action' };
              if (newData.type === undefined) newData.type = nodeType;
              const newNode = {
                id: newNodeId,
                type: nodeType,
                position: { x, y },
                data: newData,
              };
          
              const newEdge1 = {
                id: uuidv4(),
                source: source,
                target: newNodeId,
                type: 'filter',
                animated: false,
                style: { stroke: '#0069ea', strokeWidth: 2 },
                data: { text: 'empty', fields: filterFields },
              };
          
              const newEdge2 = {
                id: uuidv4(),
                source: newNodeId,
                target: target,
                type: 'filter',
                animated: false,
                style: { stroke: '#0069ea', strokeWidth: 2 },
                data: { text: 'empty', fields: filterFields },
              };
          
              return {
                payload: {
                  newNode,
                  newEdge1,
                  newEdge2,
                  removeOriginalEdge,
                  originalEdgeId,
                },
                meta: {
                    description: 'inserted step'
                  }
              };
            },
          }, 
        nodeEdgeAdded: {
            reducer(state, action) {
                action.payload.forEach((item) => state.elements.push(item))
            },
            prepare(nodeType, x, y, edgeType, source, node_id, data) {
                // Backend may pass data.newNodeId so the same id is used for interactive_auth_setup / FSM
                const requestedId = data && data.newNodeId;
                var newNodeId = requestedId || (data ? uuidv4() : uuidv4());
                const newData = data ? { ...data } : { name: 'Add action' };
                if (newData.newNodeId !== undefined) delete newData.newNodeId;
                // Match workflow_examples: ensure data.type is set so workflowTestbed/backend and frontendWorkflow stay in sync
                if (newData.type === undefined) newData.type = nodeType;

                return {
                    payload: [
                        {
                            id: newNodeId,
                            type: nodeType,
                            position: {
                                x: x,
                                y: y
                            },
                            data: newData
                        },
                        {
                            source: source,
                            target: newNodeId,
                            id: uuidv4(),
                            type: edgeType,
                            animated: false,
                            style: {
                                stroke: '#0069ea',
                                strokeWidth: 2
                            },
                            data: {
                                text: 'empty',
                                fields: filterFields
                            }
                        }
                    ],
                    meta: {
                        description: data ? 'duplicated step' : 'add step'
                      }
                }
            }
        },
        
        // Add a new reducer called nodeMoved (or you could call it insertNodeBetween if you prefer)
        // In your workflowSlice reducers:
        nodeDragged: {
            reducer(state, action) {
                state.draggedNode = action.payload
            },
            prepare(data) {
                return {payload: data }
            }
        },
        // Example: moveNodeInChain for a *linear* chain
nodeMoved: {
    reducer(state, action) {
      const { nodeId, newParent, newChild, newX, newY } = action.payload;
  
      // 1) Find the node being moved
      const nodeIndex = state.elements.findIndex((el) => el.id === nodeId);
      if (nodeIndex < 0) return; // node not found
      const node = state.elements[nodeIndex];
  
      // Optional: update the node's position if provided
      if (typeof newX === 'number' && typeof newY === 'number') {
        node.position = { x: newX, y: newY };
      }
  
      // 2) Find old parent → node edge and old node → child edge
      const oldParentEdge = state.elements.find(
        (el) => el.target === nodeId && el.source && el.type === 'filter'
      );
      const oldChildEdge = state.elements.find(
        (el) => el.source === nodeId && el.target && el.type === 'filter'
      );
  
      // We'll store oldParent and oldChild if they exist
      const oldParent = oldParentEdge ? oldParentEdge.source : null;
      const oldChild = oldChildEdge ? oldChildEdge.target : null;
  
      // 3) Remove those two edges from the array (disconnecting the node)
      state.elements = state.elements.filter(
        (el) => el.id !== oldParentEdge?.id && el.id !== oldChildEdge?.id
      );
  
      // 4) If oldParent and oldChild both exist, connect them directly
      //    (this "closes the gap" in the old chain)
      if (oldParent && oldChild) {
        state.elements.push({
          id: uuidv4(),
          source: oldParent,
          target: oldChild,
          type: 'filter',
          animated: false,
          style: { stroke: '#0069ea', strokeWidth: 2 },
          data: { text: 'empty', fields: filterFields },
        });
      }
  
      // 5) Remove the newParent → newChild edge (since we’re inserting the node)
      const edgeToRemove = state.elements.find(
        (el) => el.source === newParent && el.target === newChild
      );
      if (edgeToRemove) {
        state.elements = state.elements.filter((el) => el.id !== edgeToRemove.id);
      }
  
      // 6) Create edges newParent → node, and node → newChild
      if (newParent) {
        state.elements.push({
          id: uuidv4(),
          source: newParent,
          target: nodeId,
          type: 'filter',
          animated: false,
          style: { stroke: '#0069ea', strokeWidth: 2 },
          data: { text: 'empty', fields: filterFields },
        });
      }
      if (newChild) {
        state.elements.push({
          id: uuidv4(),
          source: nodeId,
          target: newChild,
          type: 'filter',
          animated: false,
          style: { stroke: '#0069ea', strokeWidth: 2 },
          data: { text: 'empty', fields: filterFields },
        });
      }
  
      // That’s it: we “spliced out” the node from its old spot, 
      // and “spliced it in” between newParent and newChild.
    },
    prepare({ nodeId, newParent, newChild, newX, newY }) {
      return {
        payload: { nodeId, newParent, newChild, newX, newY },
        meta: { description: 'move step' },
      };
    },
  },
         
        elementUpdated: {
            reducer(state, action) {
                state.elements = state.elements.map(el => {
                    if (el.id == action.payload.id) {
                        el.data = action.payload.data ?? el.data
                        el.position = action.payload.position ?? el.position
                    }
                    return el
                })
            },
            prepare(id, data, position, description) {
                // Accept description as 4th parameter
                // Only add meta description if explicitly provided (not null/undefined)
                // This allows us to update state without creating undo history
                const payload = {
                    payload: {
                        id: id,
                        data: data,
                        position: position
                    }
                };
                // Only add meta with description if provided
                if (description !== null && description !== undefined) {
                    payload.meta = {
                        description: description
                    };
                }
                return payload;
            }
        },
        nameUpdated: {
            reducer(state, action) {
                state.name = action.payload
            },
            prepare(name) {
                return { payload: name,
                    meta: {
                        description: 'updated name'
                      } }
            }
        },
        notification: {
            reducer(state, action) {
                state.notification = action.payload
            },
            prepare(status, message) {
                return { payload: {
                    status: status,
                    message: message
                } }
            }
        },
        filterMode: {
            reducer(state, action) {
                state.filterMode = action.payload
            },
            prepare(mode) {
                return { payload: {
                    mode: mode
                } }
            }
        },
        activeEnabled: {
            reducer(state, action) {
                state.activeEnabled = action.payload
            },
            prepare(status) {
                return { payload: status }
            }
        },
        loadingperform: {
            reducer(state, action) {
                state.loadingperform = action.payload
            },
            prepare(status) {
                return { payload: status }
            }
        },
        elementDeleted: {
            reducer(state, action) {
              const nodeToDelete = action.payload;
              // Get all incoming edges: those where the nodeToDelete is the target.
              const incomingEdges = state.elements.filter(
                (el) => el.source && el.target === nodeToDelete
              );
              // Get all outgoing edges: those where the nodeToDelete is the source.
              const outgoingEdges = state.elements.filter(
                (el) => el.source === nodeToDelete
              );
              // For every combination of incoming and outgoing, add a new edge
              incomingEdges.forEach((inEdge) => {
                outgoingEdges.forEach((outEdge) => {
                  // Only add an edge if the parent (inEdge.source) is not the same as the child (outEdge.target)
                  if (inEdge.source !== outEdge.target) {
                    // Optionally, check if an edge already exists.
                    const exists = state.elements.some(
                      (el) =>
                        el.source === inEdge.source &&
                        el.target === outEdge.target &&
                        el.type === 'filter'
                    );
                    if (!exists) {
                      state.elements.push({
                        id: uuidv4(),
                        source: inEdge.source,
                        target: outEdge.target,
                        type: 'filter',
                        animated: false,
                        style: {
                          stroke: '#0069ea',
                          strokeWidth: 2,
                        },
                        data: {
                          text: 'empty',
                          fields: filterFields, // Ensure filterFields is defined in your slice.
                        },
                      });
                    }
                  }
                });
              });
      
              // Now remove any element (node or edge) that is associated with nodeToDelete.
              state.elements = state.elements.filter((el) => {
                // Remove if the element is the node itself, or if it connects to/from it.
                if (el.id === nodeToDelete) return false;
                if (el.source === nodeToDelete || el.target === nodeToDelete)
                  return false;
                return true;
              });
            },
            prepare(elementID) {
              return { payload: elementID,
                meta: {
                    description: 'delete step'
                  } };
            },
          },
        activeUpdated: {
            reducer(state, action) {
                state.active = action.payload
            },
            prepare(active) {
                return { payload: active }
            }
        },
        elementSelected: {
            reducer(state, action) {
                state.currentElementID = action.payload
            },
            prepare(id) {
                return { payload: id }
            }
        },
        clearError: {
            reducer(state, action) {
                state.error = null
                state.errorDetails = null
            },
            prepare() {
                return { payload: null }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(fetchWorkflow.pending, (state, action) => {
            state.loading = 'loading'
        })
            .addCase(fetchWorkflow.fulfilled, (state, action) => {
                if (state.loading == 'loading') {
                    state.loading = 'loaded'
                    const newWorkflow = action.payload
                    state.elements = newWorkflow.elements
                    state.name = newWorkflow.name
                    state.id = newWorkflow.id
                    state.active = newWorkflow.active
                    // Track workflow loaded
                    if (typeof window !== 'undefined') {
                        try {
                            const { trackWorkflowLoaded } = require('../../utils/posthog');
                            trackWorkflowLoaded(newWorkflow.id, newWorkflow.name);
                        } catch (_) {
                            // PostHog utils not available, skip tracking
                        }
                    }
                }
            })            .addCase(fetchWorkflow.rejected, (state, action) => {
                if (state.loading == 'loading') {
                    state.loading = 'failed'
                    state.error = action.payload?.error || 'Failed to fetch workflow'
                    state.errorDetails = action.payload
                }
            })
            .addCase(saveWorkflow.pending, (state, action) => {
                state.loading = 'saving'
            })
            .addCase(saveWorkflow.fulfilled, (state, action) => {
                state.loading = 'saved'
            })
            .addCase(saveWorkflow.rejected, (state, action) => {
                state.loading = 'saveFailed'
            })
            .addCase(testNode.pending, (state, action) => {
                state.loading = 'testing'
            })
            .addCase(testNode.fulfilled, (state, action) => {
                state.loading = 'tested'
                const nodeID = action.payload.nodeID
                state.elements = state.elements.map(item => { return item.id == nodeID ? { ...item, data: { ...item.data, testData: action.payload.testData } } : item })
            })
             .addCase(testNode.rejected, (state, action) => {
                state.loading = 'testFailed'
                const nodeID = action.payload.nodeID
                state.elements = state.elements.map(item => { return item.id == nodeID ? { ...item, data: { ...item.data, testData: action.payload.testData } } : item })
                
            })
            .addCase(createTrigger.pending, (state, action) => {
                state.loading = 'creatingTrigger'
            })
            .addCase(createTrigger.fulfilled, (state, action) => {
                state.loading = 'createdTrigger'
                const nodeID = action.payload.nodeID
                state.elements = state.elements.map(item => { return item.id == nodeID ? { ...item, data: { ...item.data, triggerMessage: action.payload.triggerMessage } } : item })
            })
            .addCase(createTrigger.rejected, (state, action) => {
                state.loading = 'creatingTriggerFailed'
            })
            .addCase(deleteTrigger.pending, (state, action) => {
                state.loading = 'deletingTrigger'
            })
            .addCase(deleteTrigger.fulfilled, (state, action) => {
                state.loading = 'deletedTrigger'
                const nodeID = action.payload.nodeID
                state.elements = state.elements.map(item => { return item.id == nodeID ? { ...item, data: { ...item.data, triggerMessage: action.payload.triggerMessage } } : item })
            })
            .addCase(deleteTrigger.rejected, (state, action) => {
                state.loading = 'deletingTriggerFailed'
            })
            .addCase(fetchTriggerChoices.pending, (state, action) => {
                state.loading = 'fetchingTriggerChoices'
            })
            .addCase(fetchTriggerChoices.fulfilled, (state, action) => {
                state.loading = 'fetchedTriggerChoices'
                const nodeID = action.payload.nodeID
                state.elements = state.elements.map(item => {
                    if (item.id == nodeID) {
                        const existingChoices = item.data.triggerChoices || []
                        // If append is true, append new choices to existing ones; otherwise replace
                        const newChoices = action.payload.append 
                            ? [...existingChoices, ...action.payload.triggerChoices]
                            : action.payload.triggerChoices
                        return { ...item, data: { ...item.data, triggerChoices: newChoices } }
                    }
                    return item
                })
            })
            .addCase(fetchTriggerChoices.rejected, (state, action) => {
                state.loading = 'fetchTriggerChoicesFailed'
            })
            .addCase(createWorkflow.pending, (state, action) => {
                state.loading = 'creatingWorkflow'
            })
            .addCase(createWorkflow.fulfilled, (state, action) => {
                state.loading = 'createdWorkflow'
                state.elements = action.payload.initialElement
                state.id = action.payload.response.id
            })
            .addCase(createWorkflow.rejected, (state, action) => {
                state.loading = 'creatingWorkflowFailed'
            })
            .addCase(createWorkflowForAgent.fulfilled, () => {})
            .addCase(createWorkflowForAgent.rejected, () => {})
            .addCase(updateWorkflowForAgent.fulfilled, () => {})
            .addCase(updateWorkflowForAgent.rejected, () => {})
            .addCase(acceptTemplateExist.pending, (state, action) => {
                state.loading = 'acceptingTemplate'
            })
            .addCase(acceptTemplateExist.fulfilled, (state, action) => {
                if (action.payload && action.payload.needsTenantSelection) {
                    state.loading = 'needsTenantSelection'
                    state.tenantSelection = {
                        tenants: action.payload.tenants,
                        djangoId: action.payload.djangoId,
                        userName: action.payload.userName,
                        token: action.payload.token,
                        currentUser: action.payload.currentUser
                    }
                } else {
                    state.loading = 'acceptedTemplate'
                }
            })
            .addCase(acceptTemplateExist.rejected, (state, action) => {
                state.loading = 'acceptTemplateFailed'
            })
            .addCase(completeTemplateAccept.pending, (state, action) => {
                state.loading = 'completingTemplateAccept'
            })
            .addCase(completeTemplateAccept.fulfilled, (state, action) => {
                state.loading = 'completedTemplateAccept'
                state.tenantSelection = null
            })
            .addCase(completeTemplateAccept.rejected, (state, action) => {
                state.loading = 'completeTemplateAcceptFailed'
            })
            .addCase(acceptTemplateNew.pending, (state, action) => {
                state.loading = 'acceptingTemplateNew'
            })
            .addCase(acceptTemplateNew.fulfilled, (state, action) => {
                if (action.payload && action.payload.needsTenantSelection) {
                    state.loading = 'needsTenantSelection'
                    state.tenantSelection = {
                        tenants: action.payload.tenants,
                        djangoId: action.payload.djangoId,
                        scaffoldId: action.payload.scaffoldId,
                        userName: action.payload.workflowName,
                        token: action.payload.token,
                        currentUser: action.payload.currentUser
                    }
                } else {
                    state.loading = 'acceptedTemplateNew'
                }
            })
            .addCase(acceptTemplateNew.rejected, (state, action) => {
                state.loading = 'acceptTemplateNewFailed'
            })
            .addCase(createTemplate2.pending, (state, action) => {
                state.loading = 'creatingTemplate2'
            })
            .addCase(createTemplate1.pending, (state, action) => {
                state.loading = 'creatingTemplate1'
            })
            .addCase(createTemplate1.fulfilled, (state, action) => {
                if (action.payload && action.payload.needsTenantSelection) {
                    state.loading = 'needsTenantSelection'
                    state.tenantSelection = {
                        tenants: action.payload.tenants,
                        djangoId: action.payload.djangoId,
                        scaffoldId: action.payload.scaffoldId,
                        userName: action.payload.workflowName,
                        token: action.payload.token,
                        currentUser: action.payload.currentUser
                    }
                } else if (state.loading == 'loading') {
                    state.loading = 'loaded'
                    const newWorkflow = action.payload
                    if (newWorkflow && newWorkflow.elements) {
                        state.elements = newWorkflow.elements
                        state.name = newWorkflow.name
                        state.id = newWorkflow.id
                    }
                } else {
                    state.loading = 'createdTemplate1'
                }
            }).addCase(createTemplate1.rejected, (state, action) => {
                if (state.loading == 'loading') {
                    state.loading = 'failed'
                } else {
                    state.loading = 'createTemplate1Failed'
                }
            })
            .addCase(createTemplate0.pending, (state, action) => {
                state.loading = 'creatingTemplate0'
            })
            .addCase(createTemplate0.fulfilled, (state, action) => {
                if (action.payload && action.payload.needsTenantSelection) {
                    state.loading = 'needsTenantSelection'
                    state.tenantSelection = {
                        tenants: action.payload.tenants,
                        djangoId: action.payload.djangoId,
                        scaffoldId: action.payload.scaffoldId,
                        userName: action.payload.workflowName,
                        token: action.payload.token,
                        currentUser: action.payload.currentUser
                    }
                } else if (state.loading == 'loading') {
                    state.loading = 'loaded'
                    const newWorkflow = action.payload
                    if (newWorkflow && newWorkflow.elements) {
                        state.elements = newWorkflow.elements
                        state.name = newWorkflow.name
                        state.id = newWorkflow.id
                    }
                } else {
                    state.loading = 'createdTemplate0'
                }
            }).addCase(createTemplate0.rejected, (state, action) => {
                if (state.loading == 'loading') {
                    state.loading = 'failed'
                } else {
                    state.loading = 'createTemplate0Failed'
                }
            })
            .addCase(createTemplate2.fulfilled, (state, action) => {
                if (action.payload && action.payload.needsTenantSelection) {
                    state.loading = 'needsTenantSelection'
                    state.tenantSelection = {
                        tenants: action.payload.tenants,
                        djangoId: action.payload.djangoId,
                        scaffoldId: action.payload.scaffoldId,
                        userName: action.payload.workflowName,
                        token: action.payload.token,
                        currentUser: action.payload.currentUser
                    }
                } else if (state.loading == 'loading') {
                    state.loading = 'loaded'
                    const newWorkflow = action.payload
                    if (newWorkflow && newWorkflow.elements) {
                        state.elements = newWorkflow.elements
                        state.name = newWorkflow.name
                        state.id = newWorkflow.id
                    }
                } else {
                    state.loading = 'createdTemplate2'
                }
            }).addCase(createTemplate2.rejected, (state, action) => {
                if (state.loading == 'loading') {
                    state.loading = 'failed'
                } else {
                    state.loading = 'createTemplate2Failed'
                }
            })
            .addCase(createTemplate4.pending, (state, action) => {
                state.loading = 'creatingTemplate4'
            })
            .addCase(createTemplate4.fulfilled, (state, action) => {
                if (action.payload && action.payload.needsTenantSelection) {
                    state.loading = 'needsTenantSelection'
                    state.tenantSelection = {
                        tenants: action.payload.tenants,
                        djangoId: action.payload.djangoId,
                        scaffoldId: action.payload.scaffoldId,
                        userName: action.payload.workflowName,
                        token: action.payload.token,
                        currentUser: action.payload.currentUser
                    }
                } else if (state.loading == 'loading') {
                    state.loading = 'loaded'
                    const newWorkflow = action.payload
                    if (newWorkflow && newWorkflow.elements) {
                        state.elements = newWorkflow.elements
                        state.name = newWorkflow.name
                        state.id = newWorkflow.id
                    }
                } else {
                    state.loading = 'createdTemplate4'
                }
            }).addCase(createTemplate4.rejected, (state, action) => {
                if (state.loading == 'loading') {
                    state.loading = 'failed'
                } else {
                    state.loading = 'createTemplate4Failed'
                }
            })
            .addCase(createTemplate5.pending, (state, action) => {
                state.loading = 'creatingTemplate5'
            })
            .addCase(createTemplate5.fulfilled, (state, action) => {
                if (action.payload && action.payload.needsTenantSelection) {
                    state.loading = 'needsTenantSelection'
                    state.tenantSelection = {
                        tenants: action.payload.tenants,
                        djangoId: action.payload.djangoId,
                        scaffoldId: action.payload.scaffoldId,
                        userName: action.payload.workflowName,
                        token: action.payload.token,
                        currentUser: action.payload.currentUser
                    }
                } else if (state.loading == 'loading') {
                    state.loading = 'loaded'
                    const newWorkflow = action.payload
                    if (newWorkflow && newWorkflow.elements) {
                        state.elements = newWorkflow.elements
                        state.name = newWorkflow.name
                        state.id = newWorkflow.id
                    }
                } else {
                    state.loading = 'createdTemplate5'
                }
            }).addCase(createTemplate5.rejected, (state, action) => {
                if (state.loading == 'loading') {
                    state.loading = 'failed'
                } else {
                    state.loading = 'createTemplate5Failed'
                }
            })
    }
})

/** Sanitize body array: remove null/undefined/'' so they are never persisted. */
const sanitizeBodyArrayInSlice = (arr) => {
    if (!Array.isArray(arr)) return arr;
    return arr.filter((x) => x != null && x !== '');
};

/** Recursively remove null/undefined/'' from all arrays in a value (objects, arrays, nested). So workflow JSON never contains null in arrays. */
function deepSanitizeArrays(value) {
    if (value == null) return value;
    if (Array.isArray(value)) {
        return value
            .filter((x) => x != null && x !== '')
            .map((x) => deepSanitizeArrays(x));
    }
    if (typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value).map(([k, v]) => [k, deepSanitizeArrays(v)])
        );
    }
    return value;
}

/** True if this action node has a body field with at least one required-like value (e.g. name/description) filled (literal or ${...} template). */
const nodeHasBodyMapped = (node) => {
    if (!node?.data?.fields || !Array.isArray(node.data.fields)) return false;
    const bodyField = node.data.fields.find((f) => f && f.name === 'body' && f.value && typeof f.value === 'object');
    if (!bodyField?.value) return false;
    const v = bodyField.value;
    return (v.name != null && v.name !== '') || (v.description != null && v.description !== '');
};

/** Remove empty-value keys from body fields; keep empty arrays (tags: []) so backend clears them. Dedupe fields by (name, in). Deep-sanitize all array values so null/'' are never persisted. */
const scrubBodyValuesInElements = (elements) => {
    if (!Array.isArray(elements)) return elements;
    const isNode = (el) => el && el.id && !el.source && !el.target;
    const isBodyValueEmpty = (v) => {
        if (v == null || v === '') return true;
        if (Array.isArray(v) && v.length === 0) return true;
        if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return true;
        return false;
    };
    return elements.map((el) => {
        if (!isNode(el) || !el.data || !Array.isArray(el.data.fields)) return el;
        const seen = new Set();
        const newFields = el.data.fields
            .filter((f) => {
                const key = `${f?.name ?? ''}:${f?.in ?? ''}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .map((f) => {
                const rawValue = f.value;
                const sanitizedValue = deepSanitizeArrays(rawValue);
                if (f.name !== 'body' || f.schema?.type !== 'object' || !sanitizedValue || typeof sanitizedValue !== 'object' || Array.isArray(sanitizedValue)) {
                    return { ...f, value: sanitizedValue };
                }
                const scrubbed = Object.fromEntries(
                    Object.entries(sanitizedValue)
                        .map(([k, v]) => [k, Array.isArray(v) ? sanitizeBodyArrayInSlice(v) : v])
                        .filter(([, v]) => !isBodyValueEmpty(v) || (Array.isArray(v) && v.length === 0))
                );
                return { ...f, value: scrubbed };
            });
        return { ...el, data: { ...el.data, fields: newFields } };
    });
};

/** Trigger kind for data.type: only "polling" or "webhook". Never "trigger". */
const triggerDataKind = (d) =>
  (d && d.pollingParams) ? 'polling' : 'webhook';

/** Ensure each node has data.app and (for triggers) data.triggerKey so agent/check_workflow_status sees configured nodes.
 *  Schema: node.type = "trigger"|"action"; node.data.type = "polling"|"webhook" for triggers, app slug for actions. Never set data.type to "trigger". */
const ensureNodeKeysForAgent = (elements) => {
    if (!Array.isArray(elements)) return elements;
    const isNode = (el) => el && el.id && !el.source && !el.target;
    return elements.map((el) => {
        if (!isNode(el) || !el.data) return el;
        const d = el.data;
        const isTriggerStep = el.type === 'trigger' || el.type === 'polling' || d.type === 'trigger' || d.type === 'polling' || d.type === 'webhook';
        const isTrigger = isTriggerStep;
        const app = d.app != null && d.app !== '' ? d.app : (d.type || '');
        const triggerKey = isTrigger && (d.triggerKey != null && d.triggerKey !== '' ? d.triggerKey : (d.triggerResource || d.performList || d.action));
        const nextData = { ...d };
        if (app) nextData.app = app;
        if (isTrigger && triggerKey) nextData.triggerKey = triggerKey;
        // data.type: triggers = "polling"|"webhook" (never "trigger"); actions = app slug (e.g. "slack", "googledocs"), never "action"
        if (isTriggerStep) {
            if (nextData.type === 'trigger' || nextData.type == null || nextData.type === '')
                nextData.type = triggerDataKind(d);
        } else {
            // Action step: data.type must be app slug, not "action"
            if (nextData.type === 'action' || nextData.type == null || nextData.type === '')
                nextData.type = d.app || 'action';
        }
        return { ...el, data: nextData };
    });
};

const convertToBackendNodes = (flowElements) => {
    var output = {}

    // var nodeBuckets = {'isNode':isNode, 'isNotNode':(arg)=>{return !isNode(arg)}}
    // var bucketedItems = separateIntoBuckets(flowElements, nodeBuckets)
    const isNode = (node) => { if (node.target && node.source) { return false } else { return true } }

    var nodes = flowElements.filter(item => isNode(item))
    var edges = flowElements.filter(item => !isNode(item))

    nodes.forEach((node) => {
        // Safety check: ensure node.data exists
        if (!node || !node.data) {
            return; // Skip this node
        }
        
        // For triggers, use triggerResource/performList for operation (the action that powers the trigger)
        // For actions, use action field
        // CRITICAL: For polling triggers, operation should be the trigger's 'action' property (e.g., "listCardsOnBoard"),
        // NOT the trigger key (e.g., "newCard")
        let operation = node.data.action;
        if (node.data.type === 'polling' || node.data.type === 'trigger') {
            // For triggers, prefer triggerResource or performList over action
            // triggerResource/performList should be the action that powers the trigger
            if (node.data.triggerResource) {
                operation = node.data.triggerResource;
            } else if (node.data.performList) {
                operation = node.data.performList;
            }
            // If neither is set, fall back to action (which is the trigger key)
            // This shouldn't happen if Lambda is working correctly, but we need a fallback
            if (!operation) {
                operation = node.data.action;
            }
        }
        
        // workflowTestbed/workflowTemplate: node.type = trigger kind (polling|webhook) or app slug (slack, googledocs), never "trigger" or "action"
        const isTriggerStep = node.data.type === 'polling' || node.data.type === 'webhook' || node.data.type === 'trigger' || node.type === 'trigger' || node.type === 'polling';
        const backendNodeType = isTriggerStep
            ? (node.data.type === 'webhook' ? 'webhook' : 'polling')
            : (node.data.app || (node.data.type && node.data.type !== 'action' ? node.data.type : null) || 'action');
        output[node.id] = {
            id: node.id,
            name: node.data.name,
            type: backendNodeType,
            url: node.data.url,
            inputFields: { operation: operation, fields: node.data.fields, credential: node.data.credential },
            outputFields: node.data.testData ?? {},
            renderInfo: {
                x: node.position.x,
                y: node.position.y
            },
            connections: {},
            delay: 0
        }

        //assumes relevant startloops are introduced first
        //also very hacky and assuming, needs to be made robust
        if (backendNodeType === "endloop") {
            if (node.data.fields) {
                var startRef = node.data.fields[0].value
                if (startRef) {
                    var startNode = startRef.split('.')[1]
                    if (output[startNode]) {
                        if (output[startNode]['inputFields'].fields.length > 1) {
                           // output[startNode]['inputFields'][1]['value'] = node.id

                            var obj = output[startNode]['inputFields'].fields[1]
                            var newobj1 = Object.assign({}, obj ,{value : node.id})
                            var newobj2 = Object.assign({}, output[startNode]['inputFields'].fields[1], newobj1 )
                            var newobj3 = Object.assign({}, output[startNode]['inputFields'],{fields: [output[startNode]['inputFields'].fields[0], newobj2]} )
                            var newobj4 = Object.assign({}, output[startNode],{ inputFields: newobj3 } )
                            var newobj5 = Object.assign({}, output,{[startNode] : newobj4 } )
                            output = newobj5
                            
                          
                            
                          // var newfields = Object.assign(output[startNode], output[startNode]['inputFields'].fields[1],{value: node.id} )
                        }
                    }
                }
            }
        }

        var connectos = edges.filter(item => item.source == node.id)
        connectos.forEach(conn => {
            // Safely read connection filter rules; fall back to default if missing
            const connFields = conn && conn.data && Array.isArray(conn.data.fields) ? conn.data.fields : [];
            const ruleField = connFields[1];
            const ruleValue = ruleField && ruleField.value ? ruleField.value : null;
            
            // Field references should already be in Workload format (${nodes.NODEID.outputFields.FIELDNAME})
            // from the conversion in flowApp.js when filters are applied
            // Just use the rules as-is
            output[node.id]['connections'][conn.target] = (Array.isArray(ruleValue) && ruleValue.length > 0)
                ? ruleValue
                : [[{ conditionType: 'default' }]];
        })

        var delays = edges.filter(item => item.target == node.id)
        delays.forEach(delay => {
            const delayValue = delay && delay.data && Array.isArray(delay.data.fields)
                && delay.data.fields[2] && delay.data.fields[2].value !== undefined
                    ? delay.data.fields[2].value
                    : null;
            output[delay.target]['delay'] = (delayValue !== null && delayValue !== '') ? parseInt(delayValue) : 0
        })
        
        
    })

    return output
}


export const { nodeAdded, nodeDuplicated, edgeAdded, notification, activeEnabled, loadingperform, elementUpdated, nameUpdated, elementDeleted, activeUpdated, nodeEdgeAdded,nodeInserted, elementSelected, nodeMoved, nodeDragged, filterMode, clearError } = workflowSlice.actions

export default undoable(workflowSlice.reducer)

export const selectWorkflowElements = state => state.workflow.present.elements
export const selectWorkflowName = state => state.workflow.present.name
export const selectWorkflowId = state => state.workflow.present.id
export const selectWorkflowActive = state => state.workflow.present.active
export const selectWorkflowTemplateNodes = state => convertToBackendNodes(state.workflow.present.elements)
export const selectCurrentElement = state => state.workflow.present.currentElementID
export const selectDraggedNode = state => state.workflow.present.draggedNode
export const selectFilterMode = (state) => state.workflow.present.filterMode;
export const selectNotification = state => state.workflow.present.notification
export const selectActiveEnabled = state => state.workflow.present.activeEnabled
export const selectTenantSelection = state => state.workflow.present.tenantSelection
export const selectWorkflowError = state => state.workflow.present.error
export const selectWorkflowErrorDetails = state => state.workflow.present.errorDetails
export const selectWorkflowLoading = state => state.workflow.present.loading
export const selectLoadingPerform = state => state.workflow.present.loadingperform

export const selectElementById = id => state => state.workflow.present.elements.find(el => el.id == id)

export const selectParentTree = createSelector(
    selectWorkflowElements, els => {
        var output = {}

        els.forEach(item => {
            if (item.source && item.target) {
                var sourceNode = els.find(el => el.id == item.source)
                //this means it's an edge
                //get list of target's children and source's parents
                var parents = output[item.source] ?? {}
                //node already in tree
                //add parents of source and source itself
                output[item.target] = { ...output[item.target], ...parents, [item.source]: sourceNode }
                output[item.id] = { ...output[item.target] }
                Object.entries(output).map(([k, v]) => {
                    if (v.hasOwnProperty(item.target)) {
                        output[k] = { ...output[k], ...parents, [item.source]: sourceNode }
                    }
                })
            }
        })

        return output
    }
)


// export const { useGetWorkflowQuery, useCreateWorkflowQuery, useSaveWorkflowQuery } = workflowApi

